import React, { useEffect, useRef, useState, useCallback, Fragment, useContext, useMemo, forwardRef, useImperativeHandle, KeyboardEvent, EventHandler } from "react";
import { hash, toName as toDataName, useDebounce, isValidJson } from "./utils";
import { createRoot } from 'react-dom/client';
import { StrictMode } from "react";
import { Parser } from "./parser";
import { Node, Schema } from "./nodes"
import { TextArea } from "./texteditor";
import { Popover } from '@headlessui/react'
import { ClipboardDocumentIcon, ClipboardDocumentCheckIcon, Square2StackIcon, SparklesIcon, QuestionMarkCircleIcon, ArrowRightCircleIcon, PencilIcon, ArrowsPointingOutIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import json5 from "json5";
// @ts-ignore
import styling from "./styling"
import { Author, ChangeSet, createFromRawValue, createInitialState, debugIDs, getCompressedEvents, mergeStateFromEvents, Move, UserActivity, Patch, ReconstructState, reconstructStateFromEvents, SortFunction, State, TrackedArray, TrackedObject, TrackedPrimitive, TrackedRecord, updateUserActivity, Location, SortKey } from "./collab";
import { Button, useToast, Paragraph } from "@aidkitorg/component-library";
import { v4 as uuidv4 } from 'uuid';
import { doMigrations } from "./typemigrations";
import { Dropdown } from "./Components/Dropdown";
import { XMarkIcon } from "@heroicons/react/24/solid";

export const DATA_ATTRIBUTE_SUBSTRING_LENGTH = 100;

const chatModel = 'gpt-4o';

function copyToClipboardFallback(str: string) {
  const el = document.createElement("textarea");
  el.value = str;
  el.setAttribute("readonly", "");
  el.style.position = "absolute";
  el.style.left = "-9999px";
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
}

function copyToClipboard(str: string) {
  // navigator.clipboard is undefined unless inSecureContext (https)
  // so this won't work on dev safari, but will work in prod safari. (crosses fingers)
  if (navigator.clipboard !== undefined){
    navigator.clipboard.writeText(str).then(() => {
    }, () => {
      copyToClipboardFallback(str);
    });
  }else{
    copyToClipboardFallback(str);
  }
}

function Tooltip(props: {button: React.ReactElement, children: React.ReactElement}) {
  return (
    <Popover className="relative">
      <Popover.Button>{props.button}</Popover.Button>
      <Popover.Panel className="absolute z-10 mt-2 w-56 p-4 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
        {props.children}
      </Popover.Panel>
    </Popover>
  )
}

const MESSAGE_COLOR_MAP = {
  warn: 'text-amber-500',
  error: 'text-red-500',
  good: 'text-green-500',
  info: 'text-blue-500',
}

type GetMacro = (params: { macroConfig: any }, pathOverride?: string, signal?: AbortSignal) => Promise<
  { macro: any; error?: undefined } | { error: string; macro?: undefined }>

type Pastebuffer = {
  buffer?: Record<string, [() => number[], (remove: boolean) =>  TrackedObject]>,
  addToBuffer: (id: string, shape: string, locate: () => number[], slurp: (remove: boolean) => TrackedObject) => void,
  removeFromBuffer: (id: string, shape: string) => number,
  slurp(remove: boolean): TrackedObject[],
  // this is a hashed string representing the shape of the objects that are allowed to be added to the buffer
  shape?: string,

  // Quick hack to expose the ability to force a refresh
  // This is used for Unions which modfy their parent object, but don't have the
  // means (currently) to easily change parent state in a way that triggers a refresh
  rerender(): void,
}

const PastebufferContext = React.createContext<Pastebuffer>({
  addToBuffer: (id: string, shape: string, locate: () => number[], slurp: (remove: boolean) => TrackedObject) => {},
  removeFromBuffer: (id: string, shape: string) => { return 0; },
  slurp: (remove: boolean) => { return []; },

  rerender: () => {}
})

export type NodeProps = {
  node: Node,
  schema: Schema,
  value: any,
  setValue: (value: any) => void,
  trackedObject?: TrackedObject
  references?: Record<string, string[]>,
  llmContext?: string,
  author?: Author,
  changesToRender?: ChangesToRender,
  updateBreadcrumb?: (trackedObj?: TrackedObject) => void,
  onChange?: (v?: any) => void,
  readonly?: boolean
}

export function NodeElement(props: {
  node: Node,
  schema: Schema,
  trackedObject?: TrackedObject,
  onChange?: (v?:any) => void,
  path: string,
  references?: Record<string, string[]>,
  llmContext?: string,
  currentOutlines?: string[],
  getMacro?: GetMacro,
  updateBreadcrumb: (trackedObj?: TrackedObject, rootSelection?: string) => void,
  enableKeyIsValid?: EnableKeyIsValidFn,
  displayStacked?: boolean,
  changesToRender?: ChangesToRender,
  readonly?: boolean
}): JSX.Element {
  const otherEditors = (props.trackedObject?.state.objectToActivity?.[props.trackedObject.id] || [])
    .filter(editor => (editor.uid !== props.trackedObject?.state.currentAuthor?.uid || editor.tabId !== props.trackedObject?.state.currentAuthor?.tabId));
        
  // find colors for editors, and filter out which ones we've already shown (we show each color only once around the outermost
  // node of that trackedObject)    
  const editorsWithColors = otherEditors.map(ua => {
    return {
      ...ua, 
      color: stringToColour(ua.name + '/' + ua.tabId)
    }

  }).filter(ec => !(props.currentOutlines || []).includes(ec.color))

  const isAddition = (props.changesToRender?.additions || []).find(o => o.object.id === props.trackedObject?.id);
  const isRemoval = (props.changesToRender?.removals || []).find(o => o.object.id === props.trackedObject?.id);

  let newProps = {
    ...props,
    // update props currentOutlines to include the colors we're drawing so we don't draw them again.
    currentOutlines: (props.currentOutlines || []).concat(editorsWithColors.map(ec => ec.color)),

    // update changesToRender so child components only look at changes below them.
    changesToRender: {
      additions: props.changesToRender?.additions.filter(o => o.object.id !== props.trackedObject?.id) || [],
      removals: props.changesToRender?.removals.filter(o => o.object.id !== props.trackedObject?.id) || []
    }
  }

  const newUnionProps = { ...newProps }

  if (!props.node) {
    return <>TBD1</>
  }
  if (!props.trackedObject) {
    return <>TBD2</>
  }

  const innerElement = () => {
    if (props.node.meta?.component) {
      const componentName = props.node.meta.component.split(' ')[0];
      switch (componentName) {
        case 'TextArea': return React.createElement(TextArea, newProps as any);
        default: return <div>ERROR: Invalid component: {props.node.meta.component}</div>;
      }
    }

    switch(props.node.kind) {
      case 'object': {
        return React.createElement(ObjectElement, newProps as any);
      }
      case 'array': {
        return React.createElement(ArrayElement, newProps as any);
      }
      case 'ref': {
        return React.createElement(RefElement, newProps as any);
      }
      case 'string': {
        return React.createElement(StringElement, newProps as any)
      }
      case 'number': {
        return React.createElement(NumberElement, newProps as any);
      }
      case 'boolean': {
        return React.createElement(BooleanElement, newProps as any)
      }
      case 'literal': {
        return React.createElement(LiteralElement, newProps as any);
      }
      case 'union': {
        // Remove Legacy branches from unions. If there is only 1 branch left, recurse on it.
        (newUnionProps.node as typeof props.node).options = props.node.options.filter((n) => !n.meta?.isLegacy);
        if (props.node.options.length > 1) {
          return React.createElement(UnionElement, newUnionProps as any);
        } else {
          newUnionProps.node = (newUnionProps.node as typeof props.node).options[0];
          return NodeElement(newUnionProps);
        }
      }
      case 'intersection': {
        return React.createElement(IntersectionElement, newProps as any);
      }
      case 'typeParameter': {
        return <></>;
      }
      default: {
        // If there is a type error here, it means that the type of props.node is not covered
        // by the switch statement, and likely a new case is needed to match something that has
        // been added to the Node type
        let _: never = props.node;
      }
    }
    return <>Something is wrong.</>;
  }

  return (
    <div>
      <div className='flex flex-row'>
        {editorsWithColors.map(ec => {
          return <div key={ec.uid + ec.tabId}
            style={{ 
              position: 'relative', 
              backgroundColor: ec.color, 
              padding: '2px', 
              color: 'white', 
              fontSize: '12px', 
              fontWeight: '500',
              border: `3px solid ${ec.color}`,
              borderTopLeftRadius: '10px',
              borderTopRightRadius: '10px',
              width: `${100 / editorsWithColors.length}%` }}>
            {ec.name}
          </div>
        })}
      </div>
      <div
        data-tracker-id={props.trackedObject?.id} 
        data-tracker-type={props.node.kind}
        className={`${isAddition ? 'bg-green-100' : ''} ${isRemoval ? 'bg-red-100' : ''}`}
        style={(editorsWithColors.length 
          ? { border: `3px solid ${editorsWithColors[0].color}`, borderBottomLeftRadius: '10px', borderBottomRightRadius: '10px'} 
          : isAddition || isRemoval
            ? { border: `2px solid ${isAddition ? '#00a63e' : '#e7000b'}`, borderRadius: '10px'}
            : {})}>
        {innerElement()}
      </div>
    </div>
  );
}

function UnionElement(props: {
  node: Node & { kind: 'union' },
  schema: Schema,
  value: any,
  setValue: (value: any) => void,
  trackedObject?: TrackedObject,
  path: string,
  references?: Record<string, string[]>,
  llmContext?: string,
  currentOutlines?: string[],
  getMacro?: GetMacro,
  updateBreadcrumb: (trackedObj?: TrackedObject) => void,
  enableKeyIsValid?: EnableKeyIsValidFn,
  onChange?: (v?:any) => void,
  displayStacked?: boolean,
  changesToRender?: ChangesToRender,
  readonly?: boolean
}) {
  const [activeBranch, setActiveBranch] = useState<Node | null>(null);
  const [activeBranchIndex, setActiveBranchIndex] = useState<number | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchedOptions, setSearchedOptions] = useState<Node[]>(props.node.options);
  const [hoveredOptionIndex, setHoveredOptionIndex] = useState<number | null>(null);

  const pastebuffer = useContext(PastebufferContext);
  // this ref holds props.trackedObject, and helps us force a re-render if the trackedObjects and the props are not in sync
  const ref = useRef<any>(undefined);
  // reference to the options dropdown for element selection - used for closing it when we click outside
  const optionsRef = useRef<any>(undefined);
  // Sometimes props come in after the object is located, the prop count makes us robust to this
  const propCount = useRef<any>(undefined);

  // Force rerender
  if (ref.current !== props.trackedObject || (propCount.current !== Object.keys(props.trackedObject?.value).length)) {
    ref.current = props.trackedObject;
    propCount.current = Object.keys(props.trackedObject?.value).length;
    let i = 0; 
    let newBranch: Node | null = null;
    for (const branch of props.node.options) {
      if (checkMatches(branch, props.schema, props.trackedObject!)) {
        newBranch = branch;
        break;
      } 
      i += 1;
    }
    
    if (!newBranch && props.trackedObject) {
      console.error("[UnionElement] No branch matches, getting diagnostics", props.trackedObject.read());
      const diagnostics = props.node.options.map((opt, idx) => ({
        diagnostic: getMatchDiagnostics(opt, props.schema, props.trackedObject!, props.path)
      }));

      const closestMatch = diagnostics.reduce((best, current) => 
        current.diagnostic.depth > best.diagnostic.depth ? current : best
      );
      
      const fp = closestMatch.diagnostic.failurePoint;
      return (
        <div style={{ 
          padding: '8px', 
          background: '#fee', 
          border: '1px solid #c00',
          borderRadius: '4px',
          fontSize: '12px',
          fontFamily: 'monospace'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            ERROR: No union branch matches
          </div>
          <div style={{ marginLeft: '8px' }}>
            {fp && (
              <>
                <div style={{ marginTop: '4px', fontWeight: 'bold' }}>Failure at: {fp.path}</div>
                <div style={{ marginLeft: '8px' }}>
                  <div>Reason: {fp.reason}</div>
                  <div>Expected: {fp.expected}</div>
                  <div>Actual: {fp.actual}</div>
                </div>
              </>
            )}
          </div>
        </div>
      );
    }
    
    setTimeout(() => {
      setActiveBranch(newBranch);
      setActiveBranchIndex(i);
      setSearchedOptions(props.node.options);
    }, 0);
  }

  // Handle the click outside of the element type selection, to close the selecting dropdown
  useEffect(() => {    
    const handleClickOutside = (e: MouseEvent) => {
      if (!selecting) return;
      if (optionsRef.current && !optionsRef.current.contains(e.target)) {
        setSelecting(false);
        setSearchedOptions(props.node.options);
      }
    }
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [optionsRef, selecting]);

  // Helper to select the chosen element when it's clicked or selected by key press
  const selectBranch = (i: number, node: Node) => {
    // Insert old values into the prototype
    // The prototype is the template for the new branch we are selecting (a new kind within a union)
    // The trackedObject is the current state of the old value we are switching from (the old kind)
    //
    // To maintain as much overlap between the old and new kind, we traverse and recurse down the old
    // type's attributes and assign any existing values to the prototype and then we'll create
    // the new kind with a pre-populated prototype that preserves any identical values.
    //
    // allowKind: The first evaluation of updateProtoWithTrackedObject needs to ignore the `kind` attribute
    // because the user is selecting a branch and therefore, switching the kind. Every subsequent recursive call
    // should allow kind because we want to preserve any relevant union types within the parent type.
    const schemaValues = Object.values(props.schema);
    const updateProtoWithTrackedObject = (proto: any, trackedObject: any, allowKind: boolean = false) =>  {
      if (!trackedObject || typeof trackedObject !== 'object') {
        return proto;
      }

      const oldKeys = Object.keys(trackedObject);
      const isObjectKind = (node: Node) => node?.kind === 'object';
      const isLiteralNode = (node: Node) => node?.kind === 'literal';

      // Find the distro schema definition for the new kind. We need this to know
      // what properties we are able to copy over from the old object and what 
      // properites are required.
      let newNode: Node | undefined;
      let oldNode: Node | undefined;
      for (const value of schemaValues) {
        if (!isObjectKind(value)) continue;
        const propNode = (value as any).props?.kind;
        if (propNode && isLiteralNode(propNode)) {
          if (propNode.value === proto?.kind) {
            newNode = value;
          } 
          if (propNode.value === trackedObject.kind?.value || propNode.value === trackedObject.kind) {
            oldNode = value;
          }
        }
      }
            
      // Determine the keys to evaluate based on the new kind's schema or the prototype's keys.
      // If the prototype is not a top-level distro kind, just use the provided prototype's keys as the
      // set of allowed keys.
      const newKeys = (newNode && newNode.kind === 'object') ? Object.keys(newNode.props) : Object.keys(proto);
      let keysToEvaluate = oldKeys.filter(item => newKeys.includes(item));

      // If we are switching kind, that means we want to maintain the oldNode type, instead of going to newNode.
      // Our prototype for the newNode might not have the same required keys. 
      // If that's the case, we need to use a prototype based on the oldNode, not the newNode.
      if (oldNode && allowKind && keysToEvaluate.includes('kind')) {
        const oldRequiredProps: string[] = (oldNode as any)?.required || [];
        const needsNewPrototype = !oldRequiredProps.every(item => newKeys.includes(item));
        if (needsNewPrototype) {
          proto = createPrototype(oldNode, props.schema);

          // if we switched to a new proto, our newNode and keysToEvaluate need to match this new prototype
          for (const value of schemaValues) {
            if (!isObjectKind(value)) continue;
            const propNode = (value as any).props?.kind;
            if (propNode && isLiteralNode(propNode)) {
              if (propNode.value === proto?.kind) {
                newNode = value;
              }
            }
          }
          const newerKeys = (newNode && newNode.kind === 'object') ? Object.keys(newNode.props) : Object.keys(proto);
          keysToEvaluate = oldKeys.filter(item => newerKeys.includes(item));
        }
      }

      for (const key of keysToEvaluate) {
        if ((!allowKind && key === 'kind') || key === 'modeType' || key === 'components' || key === 'mode') continue;
                
        let obj = trackedObject[key];

        // don't allow replacing if oldNode props[key] kind doesn't match newNode props[key] kind
        // console.log("Evaluating", key, (oldNode as any)?.props?.[key]?.kind, (newNode as any)?.props?.[key]?.kind)
        if (oldNode?.kind === 'object' && newNode?.kind === 'object' && oldNode.props[key]?.kind !== newNode.props[key]?.kind) {
          continue;
        }

        if ((typeof obj === 'object' && !Array.isArray(obj) && obj.read) || typeof obj !== 'object') {
          let oldValue = typeof obj === 'object' ? obj.read() : obj;

          if (oldValue && typeof proto[key] === 'object') {
            if (!Array.isArray(proto[key])) {
              // If the new prototype is an object, recurse down the object's keys and update the prototype accordingly.
              proto[key] = updateProtoWithTrackedObject(proto[key], oldValue, true);
            } else if (newNode && newNode.kind === 'object' && Array.isArray(proto[key]) && Array.isArray(oldValue) && oldValue.every((x) => typeof x === 'object' && x !== null)) {
              // If the new prototype is an array of objects, only copy the objects if all the required keys of the new prototype are present in the old value.
              const newRequiredProps: string[] = (newNode as any).props[key].element?.required || [];
              proto[key] = oldValue.filter(obj => newRequiredProps.every((key: string) => key in obj));
            }
          }
          // only literals should be directly replaced on the prototype 
          else if (oldNode?.kind === 'literal' && newNode?.kind === 'literal') {
            proto[key] = oldValue;
          }
        } else if (Array.isArray(obj) && Array.isArray(proto[key])) {
          const newRequiredProps: string[] = (newNode as any).props[key].element?.required || [];
          proto[key] = obj.filter(element => newRequiredProps.every((key: string) => key in element));
        }
      }
      return proto;
    };

    let proto = createPrototype(node, props.schema);
    proto = updateProtoWithTrackedObject(proto, props.trackedObject!.value);
        
    const newElement = createFromRawValue(props.trackedObject!.state, proto);

    // We need to find the location of the current object and replace it with the new one
    const locations = props.trackedObject!.state.objects[props.trackedObject!.id].locations;
    const latest = locations.reduce((acc, cur): [number, typeof locations[number] | null] => {
      if (cur.prev_gen > acc[0]) {
        return [cur.prev_gen, cur];
      }
      return acc;
    }, [-1, null] as [number, typeof locations[number] | null])[1];

    if (latest?.loc?.kind === 'object') {
      const parent = props.trackedObject?.state.workingSet[latest.loc.id] as TrackedRecord;
      parent.remove(latest.loc.prop);
      parent.set(latest.loc.prop, newElement);
    } else if (latest?.loc?.kind === 'array') {
      const parent = props.trackedObject?.state.workingSet[latest.loc.id] as TrackedArray;
      const idx = parent.value.indexOf(props.trackedObject!);
      parent.remove(idx);
      parent.insert(idx, newElement);
    }

    newElement.locateUserHere();

    setActiveBranch(node);
    setActiveBranchIndex(i);
    setSelecting(false);

    pastebuffer.rerender();
    props.onChange?.();
  }

  // Track keypresses when selecting so the user can make their selection through arrow keys
  useEffect(() => {
    const handleKeyPress : EventHandler<any> = (e: KeyboardEvent) => {
      if (selecting) {
        if (e.key === 'ArrowUp') {
          const newIndex = hoveredOptionIndex !== null ? hoveredOptionIndex - 1 : -1;
          // all this fancy footwork below is to make sure the mod is always positive.
          setHoveredOptionIndex((newIndex + searchedOptions.length) % searchedOptions.length);
        } else if (e.key === 'ArrowDown') {
          const newIndex = hoveredOptionIndex !== null ? hoveredOptionIndex + 1 : 0;
          setHoveredOptionIndex((newIndex + searchedOptions.length) % searchedOptions.length);
        } else if (e.key === 'Enter' && hoveredOptionIndex !== null) {
          setHoveredOptionIndex(null);
          selectBranch(hoveredOptionIndex, searchedOptions[hoveredOptionIndex]);
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [selecting, hoveredOptionIndex, searchedOptions]);

  // If the UnionElement is newlyAdded, we start with the selection dropdown open.
  useEffect(() => {
    if (props.trackedObject?.newlyAdded) {
      if (props.trackedObject?.parent instanceof TrackedArray) {
        setSelecting(true);
      }

      props.trackedObject.newlyAdded = false;
    }
  }, [props.trackedObject, activeBranch]);

  // Filter the options in the dropdown based on the users search
  const handleFilterOptions = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value.toLowerCase();

    let filteredOptions = props.node.options.filter((n => {
      const desc = getDescription(n, props.schema);
      return desc.toLowerCase().startsWith(newVal);
    }));

    setSearchedOptions(filteredOptions);
    setSearchText(newVal);
    setHoveredOptionIndex(null);
  }, [props.node]);

  // When the user clicks an option in the dropdown, set it as the active branch
  const handleOptionClick = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>, i: number) => {
    e.stopPropagation();

    if (!selecting) {
      setSelecting(true); 
      return;
    }

    selectBranch(i, searchedOptions[i]);
  }, [searchedOptions, selecting, selectBranch]);

  return <div className="flex">
    {!props.readonly && (
      <div className="pt-1 group" ref={optionsRef}>
        {selecting &&
                  <input
                    type="text"
                    onClick={(e) => { e.stopPropagation(); }}
                    value={searchText}
                    onChange={handleFilterOptions}
                    placeholder="Search"
                    className="border ml-3"
                    autoFocus
                  />
        }
        {/* We probably shouldn't render every one of these if the user has not clicked the gray circle button */}
        {searchedOptions.map((o, i) =>
          o.meta?.hidden ? <></> :
            <div key={i}
              onClick={(e) => handleOptionClick(e, i)}
              className={`flex hover:bg-gray-200 cursor-pointer ${hoveredOptionIndex === i ? 'bg-gray-200' : ''}`}>
              {activeBranchIndex === i ?
                <div className="border-2 m-2 border-slate-200 w-3 h-3 bg-slate-200 rounded-full"> </div>
                : <div className={(selecting ? "block" : "hidden") + " border-2 m-2 border-white w-3 h-3 bg-gray-200 rounded-full"}> </div>}
              <div className={selecting ? "block" : "hidden"}>{getDescription(o, props.schema)}</div>
            </div>
        )}
      </div>
    )}
    <div className="flex-grow">
      {activeBranch &&
                <NodeElement
                  node={{
                    ...activeBranch,
                    meta: {
                      ...activeBranch.meta,
                      ...props.node.meta
                    }
                  }}
                  onChange={props.onChange}
                  schema={props.schema}
                  trackedObject={props.trackedObject}
                  path={props.path}
                  references={props.references}
                  llmContext={props.llmContext}
                  currentOutlines={props.currentOutlines}
                  getMacro={props.getMacro}
                  updateBreadcrumb={props.updateBreadcrumb}
                  displayStacked={props.displayStacked}
                  changesToRender={props.changesToRender}
                  enableKeyIsValid={props.enableKeyIsValid}
                  readonly={props.readonly}
                />
      }
    </div>
  </div>
}

function IntersectionElement(props: {
  node: Node & {kind: 'intersection' },
  schema: Schema,
  value: any,
  setValue: (value: any) => void,
  trackedObject?: TrackedObject,
  path: string,
  references?: Record<string, string[]>,
  llmContext?: string,
  currentOutlines?: string[],
  getMacro?: GetMacro,
  updateBreadcrumb: (trackedObj?: TrackedObject) => void,
  enableKeyIsValid?: EnableKeyIsValidFn,
  onChange?: (v:any) => void,
  displayStacked?: boolean,
  changesToRender?: ChangesToRender
}) {
  const allProps: Record<string, Node> = {};
  const allRequired: string[] = [];
  const allPropMeta: Record<string, any> = {};
  const allMeta: Record<string, any> = {};
  const unmergeableConstraints: Node[] = [];
  
  for (const constraint of props.node.constraints) {
    if (constraint.kind === 'object') {
      Object.assign(allProps, constraint.props);
      allRequired.push(...constraint.required);
      Object.assign(allPropMeta, constraint.propMeta || {});
      Object.assign(allMeta, constraint.meta || {});
    } else if (constraint.kind === 'ref') {
      const resolved = props.schema[constraint.label];
      if (resolved && resolved.kind === 'object') {
        Object.assign(allProps, resolved.props);
        allRequired.push(...resolved.required);
        Object.assign(allPropMeta, resolved.propMeta || {});
        Object.assign(allMeta, resolved.meta || {});
      } else {
        unmergeableConstraints.push(constraint);
      }
    } else {
      unmergeableConstraints.push(constraint);
    }
  }
  
  const hasMergedProps = Object.keys(allProps).length > 0;
  
  return <div>
    {hasMergedProps && <NodeElement
      node={{
        kind: 'object',
        props: allProps,
        required: [...new Set(allRequired)],
        propMeta: allPropMeta,
        meta: { ...allMeta, ...props.node.meta }
      }}
      onChange={props.onChange}
      schema={props.schema}
      trackedObject={props.trackedObject}
      path={props.path}
      references={props.references}
      llmContext={props.llmContext}
      currentOutlines={props.currentOutlines}
      getMacro={props.getMacro}
      updateBreadcrumb={props.updateBreadcrumb}
      displayStacked={props.displayStacked}
      changesToRender={props.changesToRender}
      enableKeyIsValid={props.enableKeyIsValid}
    />}
    {unmergeableConstraints.map((c, i) => 
      <NodeElement
        key={i}
        node={c}
        onChange={props.onChange}
        schema={props.schema}
        trackedObject={props.trackedObject}
        path={props.path}
        references={props.references}
        llmContext={props.llmContext}
        currentOutlines={props.currentOutlines}
        getMacro={props.getMacro}
        updateBreadcrumb={props.updateBreadcrumb}
        displayStacked={props.displayStacked}
        changesToRender={props.changesToRender}
        enableKeyIsValid={props.enableKeyIsValid}
      />)}
  </div>;
}

var stringToColour = function(str: string) {
  if (!str) return 'hsl(0%,0%,0%)';
    
  var hash = 0;
  for (var i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  var colour = 'hsl(' + (hash % 360) + ', 60%, 70%)';
  return colour;
}

const tailwindBgColorMap: Record<string, string> = {
  red: 'bg-red-200',
  orange: 'bg-orange-200',
  yellow: 'bg-yellow-200',
  green: 'bg-green-200',
  blue: 'bg-blue-200',
  violet: 'bg-violet-200',
  fuchsia: 'bg-fuchsia-200',
};

function LiteralElement(props: { 
  node: Node & { kind: 'literal'}, 
  schema: Schema,
  value: any,
  setValue: (value: any) => void,
  trackedObject?: TrackedPrimitive,
  path: string,
  references?: Record<string, string[]>,
  llmContext?: string,
  getMacro?: GetMacro,
  updateBreadcrumb: (trackedObj?: TrackedObject) => void
}) {
  const showTailwindColor = !!props.node.meta?.tailwindColor && props.node.value in tailwindBgColorMap;
  return <div
    onClick={() => props.updateBreadcrumb(props.trackedObject)}
    className={"rounded-full text-sm px-2 py-1 inline-block " + (showTailwindColor ? tailwindBgColorMap[props.node.value] : '')}
    style={showTailwindColor ? {} : {"backgroundColor": stringToColour(props.node.value)}}>
    {props.node.value}
  </div>
}

function StringElement(props: {
  node: Node & { kind: 'string' },
  schema: Schema,
  value: any,
  onChange?: (v?:any) => void,
  setValue: (value: any) => void,
  trackedObject?: TrackedPrimitive,
  path: string,
  references?: Record<string, string[]>,
  llmContext?: string,
  getMacro?: GetMacro,
  updateBreadcrumb: (trackedObj?: TrackedObject) => void,
  enableKeyIsValid?: EnableKeyIsValidFn,
  readonly?: boolean
}) {
  const [inputValue, setInputValue] = useState(props.trackedObject!.value);
  const [inputValid, setInputValid] = useState(true);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const prevMetaInputValid = useRef<boolean>(props.node.meta?.inputValid);
  const { toast } = useToast();
  const toastRef = useRef<ReturnType<typeof toast> | null>(null);
  const isMetaEnabled = props.node.meta?.inputValid;

  const setTrackedObject = (value: any) => {
    (props.trackedObject! as TrackedPrimitive).set(value);
    props.trackedObject?.locateUserHere();
  }
  const [debouncedSetTrackedObject, cancelDebouncedSetTrackedObject] = useDebounce((value: any) => {
    setTrackedObject(value)
    props.onChange?.();
  }, 500);

  const handleInputEvent = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    handleInputChange(e.target.value);
  };

  const handleInputChange = (str: string) => {
    if (props.node.meta?.format) {
      const regex = new RegExp(props.node.meta.format);
      setInputValid(regex.test(str));
    }
    setInputValue(str);
    debouncedSetTrackedObject(str);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement> | React.FocusEvent<HTMLSelectElement>) => {
    if (props.node.meta?.format) {
      const regex = new RegExp(props.node.meta.format);
      setInputValid(regex.test(e.target.value));
    }
    if (e.target.value !== props.trackedObject?.read()) {
      cancelDebouncedSetTrackedObject();
      setTrackedObject(e.target.value);
    }
  };

  useEffect(() => {
    const viewingAuthor = props.trackedObject?.state.currentAuthor;
    const editingAuthorsNotMe = (props.trackedObject?.state.objectToActivity?.[props.trackedObject?.id] || []).some(
      ea => (ea.uid !== viewingAuthor?.uid || ea.tabId !== viewingAuthor?.tabId));

    // this can cause cursor jumping issues if we are the one editing, and then our own changes get propogated through. 
    // we only really care for this useEffect when someone else is also editing the text field.
    if (editingAuthorsNotMe) {
      if (inputValue !== props.trackedObject!.value) {
        setInputValue(props.trackedObject!.value);
        if (inputRef.current) {
          inputRef.current.value = props.trackedObject!.value;
        }
      }
    }
  }, [inputRef.current, props.trackedObject!.value]);

  useEffect(() => {
    if (prevMetaInputValid.current && !isMetaEnabled) {
      // if a change causes this to go from enabled -> disabled, show error message to user
      const kindLabel = props.trackedObject?.parent?.value?.kind?.value ?? 
        (props.trackedObject?.parent?.value?.suffix?.value ? 'Notification Followup' : "Unknown kind");
      const nameLabel = props.trackedObject?.parent?.value?.name?.value ?? 
        props.trackedObject?.parent?.value?.targetField?.value ?? 
        props.trackedObject?.parent?.value?.suffix?.value ?? "Unknown name";
      const t = toast({
        title: `Disabled ${kindLabel}: ${nameLabel}`,
        variant: 'error',
        description: <Paragraph size="sm">A change has disabled this {kindLabel} in your current draft. <b>NOTE: you must publish your survey to fully disable this.</b></Paragraph>,
        action: <Button onClick={() => t.dismiss()}>I understand</Button>,
        autoClose: false
      });
      toastRef.current = t;
    }
    else if (!prevMetaInputValid.current && isMetaEnabled) {
      // if a change causes this to go from disabled -> enabled, check if it is a followup.
      // If it's a followup, we need to toast if the OG notification is not enabled.
      const parent = props.trackedObject?.parent?.read();
      const greatGrandParent = props.trackedObject?.parent?.parent?.parent?.read();

      if (greatGrandParent?.kind === 'Notification' && !!parent?.suffix) {
        const parentNofifIsEnabled = props.enableKeyIsValid?.(greatGrandParent?.enableKey, greatGrandParent, { allowVersions: ['legacy'] });

        if (!parentNofifIsEnabled) {
          const t = toast({
            title: `Followup Not Enabled: ${greatGrandParent?.targetPrefix}_${parent?.suffix}`,
            variant: 'error',
            description: <Paragraph size="sm">The parent notification ({greatGrandParent?.name}) for this followup is not enabled. <b>A followup will not be sent if the parent is disabled.</b></Paragraph>,
            action: <Button onClick={() => t.dismiss()}>I understand</Button>,
            autoClose: false
          });
          toastRef.current = t;
        }
      }
    }
    else if(toastRef.current && isMetaEnabled) {
      toastRef.current.dismiss();
    }
    prevMetaInputValid.current = isMetaEnabled;
  }, [isMetaEnabled])

  if (props.node.meta?.references && ((props.references || {})[props.node.meta.references] || []).length) {
    return <>
      <div className="flex" onClick={() => props.updateBreadcrumb(props.trackedObject)}>
        <input
          ref={inputRef}
          dir='auto'
          className="w-full border"
          style={
            {
              backgroundColor: props.trackedObject?.value && ((props.references || {})[props.node.meta.references] || []).includes(props.trackedObject!.value) ? "rgba(220, 255, 220, 0.7)" : "transparent",
              marginLeft: '10px',
              ...(props.node.meta?.direction === 'rtl' ? {
                direction: props.node.meta.direction as "rtl" | "ltr",
              } : {})}
          }
          defaultValue={inputValue}
          onChange={handleInputEvent}
          onBlur={handleBlur}
          readOnly={props.readonly}
        />
        <Dropdown 
          options={((props.references || {})[props.node.meta.references] || []).map(o => {
            return {
              name: o,
              callback: () => {
                handleInputChange(o);
                // Update the input UI element to show the selected value
                if (inputRef.current) {
                  inputRef.current.value = o;
                }
              }
            }
          })}
          dropdownOrigin='right'
          searchable
        />
      </div>
      {!inputValid && <div className="ml-3 text-red-500 text-xs">Your input is invalid. {props.node.meta?.formatMessage}</div>}
    </>
  }

  return <>
    <input
      dir='auto'
      ref={inputRef}
      className="w-full border"
      style={{
        backgroundColor: (props.node.meta && 'inputValid' in props.node.meta)
          ? (props.node.meta?.['inputValid'] ? 'rgba(220, 255, 220, 0.7)' : 'rgba(255, 220, 220, 0.7)')
          : 'transparent',
        ...(props.node.meta?.direction === 'rtl' ? {
          direction: props.node.meta.direction as "rtl" | "ltr",
        } : {})
      }
      }
      defaultValue={inputValue}
      onChange={handleInputEvent}
      onBlur={handleBlur}
      readOnly={props.readonly}
    />
    {!inputValid && <span className="text-red-500 text-xs">Your input is invalid. {props.node.meta?.formatMessage}</span>}
  </>
}

function NumberElement(props: { 
  node: Node & { kind: 'string' }, 
  schema: Schema,
  value: any,
  setValue: (value: any) => void,
  trackedObject?: TrackedPrimitive,
  path: string
  references?: Record<string, string[]>,
  llmContext?: string,
  getMacro?: GetMacro,
  updateBreadcrumb: (trackedObj?: TrackedObject) => void,
  readonly?: boolean
}) {
  const [inputValue, setInputValue] = useState<string>(props.trackedObject?.value?.toString() || '');
  const defaultInputValue = props.node.meta?.default || '0';
  const isValidNumber = (value: string) => !isNaN(parseFloat(value));
  const setTrackedObject = (value: number) => {
    (props.trackedObject! as TrackedPrimitive).set(value);
    props.trackedObject?.locateUserHere();
  }
  const [debouncedSetTrackedObject, cancelDebouncedSetTrackedObject] = useDebounce((value: number) => {
    setTrackedObject(value);
  }, 500);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (isValidNumber(e.target.value)) {
      debouncedSetTrackedObject(parseFloat(e.target.value));
    }
  };

  const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    // If the value is invalid, set it to the default and let the user know
    if (!isValidNumber(e.target.value)) {
      cancelDebouncedSetTrackedObject();
      alert(`Oops, that's not a valid number!\n\nSetting a default value of ${defaultInputValue}. If that won't work for you, please enter a number.`)
      setInputValue(defaultInputValue);
      if (defaultInputValue !== props.trackedObject?.value?.toString()) 
        props.trackedObject?.locateUserHere();
      (props.trackedObject! as TrackedPrimitive).set(parseFloat(defaultInputValue));
    } else if (e.target.value !== props.trackedObject?.value?.toString()) {
      cancelDebouncedSetTrackedObject();
      setTrackedObject(parseFloat(e.target.value));
    }
  }

  useEffect(() => {
    if (inputValue !== props.trackedObject?.value?.toString()) {
      setInputValue(props.trackedObject?.value?.toString() || '');
    }
  }, [props.trackedObject?.value]);

  return (
    <>
      <input
        className="border w-20 read-only:bg-transparent"
        type="number"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onClick={() => props.updateBreadcrumb(props.trackedObject)}
        readOnly={props.readonly}
      />
      {!isValidNumber(inputValue) && <span className="text-red-500 text-xs">Please enter a number.</span>}
    </>
  )
}

function BooleanElement(props: {
  node: Node & { kind: 'string' },
  schema: Schema,
  value: any,
  setValue: (value: any) => void,
  trackedObject?: TrackedPrimitive,
  path: string
  references?: Record<string, string[]>,
  llmContext?: string,
  getMacro?: GetMacro,
  updateBreadcrumb: (trackedObj?: TrackedObject) => void,
  readonly?: boolean
}) {
  const [rerender, setRerender] = useState(0);
  return <input
    onClick={() => props.updateBreadcrumb(props.trackedObject)}
    checked={props.trackedObject?.value === true}
    onChange={(e: any) => {
      props.trackedObject!.set(e.target.checked);
      props.trackedObject?.locateUserHere();
      setRerender(rerender + 1);
    }}
    type="checkbox"
    disabled={props.readonly}
  />
}

function RefElement(props: {
  node: Node & { kind: 'ref'},
  schema: Schema,
  value: any,
  setValue: (value: any) => void,
  trackedObject?: TrackedObject,
  path: string
  references?: Record<string, string[]>,
  llmContext?: string,
  currentOutlines?: string[],
  getMacro?: GetMacro,
  updateBreadcrumb: (trackedObj?: TrackedObject) => void,
  enableKeyIsValid?: EnableKeyIsValidFn,
  onChange: (v?:any) => void,
  changesToRender?: ChangesToRender,
  displayStacked?: boolean,
  readonly?: boolean
}) {
  return <div className="w-full">
    <NodeElement
      node={props.schema[props.node.label]}
      schema={props.schema}
      trackedObject={props.trackedObject}
      path={props.path}
      references={props.references}
      llmContext={props.llmContext}
      currentOutlines={props.currentOutlines}
      getMacro={props.getMacro}
      updateBreadcrumb={props.updateBreadcrumb}
      onChange={props.onChange}
      displayStacked={props.displayStacked}
      enableKeyIsValid={props.enableKeyIsValid}
      changesToRender={props.changesToRender}
      readonly={props.readonly}
    />
  </div>
}

function ObjectElement(props: {
  node: Node & { kind: 'object' },
  schema: Schema,
  //value: any,
  setValue: (value: any) => void,
  trackedObject?: TrackedRecord,
  path: string,
  references?: Record<string, string[]>,
  llmContext?: string,
  currentOutlines?: string[],
  getMacro?: GetMacro,
  displayStacked?: boolean,
  changesToRender?: ChangesToRender,
  updateBreadcrumb: (trackedObj?: TrackedObject, rootSelection?: string) => void,
  enableKeyIsValid?: EnableKeyIsValidFn
  onChange: (v?:any) => void,
  readonly?: boolean
}) {
  const style =  props.node.meta?.display || (props.displayStacked ? 'stacked' : 'default');
  let outerStyle = "block border border-slate-400 flex flex-wrap " + (['stacked', 'tabs'].includes(style) ? 'min-w-fit' : 'min-w-full');
  let innerStyle = "flex-grow " + ((props.displayStacked || props.node.meta?.display === 'stacked') ? "w-full" : "");
  let [shownProps, setShownProps] = useState<Record<string, boolean>>({});

  // Filter any hidden props to select the default
  // Sets 'en' as default selected (for RichText)
  // Sets 'survey' as default for Distro root
  const defaultSelected = () => {
    if (props.trackedObject?.id === 'root' && props.node.required.includes('survey')) {
      return 'survey';
    }
    return Object.keys(props.trackedObject!.value)
      .filter(k => k !== '_id' && props.node.propMeta?.[k]?.hidden !== 'yes')
      .sort((a,b) => a === 'en' ? -1 : 0)[0] || '';
  };
  let [selectedProp, setSelectedProp] = useState<string>(defaultSelected);

  const [rerender, setRerender] = useState(0);
  const { toast } = useToast();
  const [showComment, setShowComment] = useState<Record<string, boolean>>({});

  const width = {
    '1': (props.displayStacked || props.node.meta?.display === 'stacked') ? "w-full" : 'flex-grow w-1/4',
    '2': (props.displayStacked || props.node.meta?.display === 'stacked') ? "w-full" : 'flex-grow w-1/2',
    '3': (props.displayStacked || props.node.meta?.display === 'stacked') ? "w-full" : 'flex-grow w-3/4',
    '4': 'w-full',
  } as Record<string, string>;
    
  let renderedProps: Record<string, true> = {};
  let removedProps: Record<string, true> = {};
  let addedProps: Record<string, true> = {};
    
  function removePropNamed(p: string) {
    if (!props.node.required.includes(p)) {
      const confirmed = window.confirm('Are you sure you want to delete this?');
      if (confirmed) {
        props.trackedObject!.remove(p);
        setRerender(rerender + 1);
      }
    }
  }

  function addPropNamed(o: string) {
    const prototype = createPrototype(props.node.props[o], props.schema);
    const newElement = createFromRawValue(props.trackedObject!.state, prototype);
    props.trackedObject!.set(o, newElement);
    setRerender(rerender + 1);
  }

  // If there are removals in our changesToRender that belong to this object, we want to render them.
  const objectToRender = { ...props.trackedObject?.value };
  for (const removal of props.changesToRender?.removals || []) {
    if (removal.location?.kind === 'object' && removal.location.id === props.trackedObject?.id) {
      objectToRender[removal.location.prop] = removal.object;
      removedProps[removal.location.prop] = true;
    }
  }
  for (const addition of props.changesToRender?.additions || []) {
    if (addition.location?.kind === 'object' && addition.location.id === props.trackedObject?.id) {
      addedProps[addition.location.prop] = true;
    }
  }

  // Check if this entire object is a Reference type and find the original object
  const currentObjectValue = props.trackedObject?.read?.();
  const isReferenceObject = currentObjectValue?.kind?.endsWith(' Reference');

  // Use useMemo to avoid recomputing on every render
  // State for lazy-loaded reference display
  const [referencedObjectSnapshot, setReferencedObjectSnapshot] = useState<{
    trackedObject: TrackedObject;
    node: Node;
    referenceType: string;
    originalObjectId: string;
    referenceLabel: string;
    referenceId?: string;
  } | null>(null);

  // Compute reference metadata synchronously (fast)
  const referenceMetadata = useMemo(() => {
    if (!isReferenceObject || !props.trackedObject) {
      return null;
    }

    const definitionId = currentObjectValue?.definitionId;

    if (!definitionId) return null;

    const referenceType = currentObjectValue.kind;
    const blockKind = referenceType.replace(' Reference', '');

    // Search through the root trackedObject to find the definition by ID
    const findDefinitionComponent = (obj: any, id: string): TrackedObject | null => {
      if (!obj) return null;

      const readValue = typeof obj.read === 'function' ? obj.read() : obj.value;

      // Check if this object itself matches our criteria
      if (readValue && typeof readValue === 'object' && readValue.definitionProps?.kind === 'Definition') {
        const matches = readValue.kind === blockKind && readValue.definitionProps.id === id;

        if (matches) {
          return obj;
        }
      }

      // Recursively search children
      if (obj.value !== undefined) {
        if (Array.isArray(obj.value)) {
          for (const item of obj.value) {
            const result = findDefinitionComponent(item, id);
            if (result) return result;
          }
        } else if (typeof obj.value === 'object' && obj.value !== null) {
          for (const key of Object.keys(obj.value)) {
            const result = findDefinitionComponent(obj.value[key], id);
            if (result) return result;
          }
        }
      }

      return null;
    };

    let root: TrackedObject | undefined = props.trackedObject;
    while (root?.parent) {
      root = root.parent;
    }

    const originalTrackedObject = findDefinitionComponent(root, definitionId);

    if (!originalTrackedObject) return null;

    // Get the label from the found Definition
    const definitionLabel = originalTrackedObject.read()?.definitionProps?.label || 'Unnamed';

    return {
      originalTrackedObject,
      referenceType,
      referenceLabel: definitionLabel,
      referenceId: definitionId
    };
  }, [props.trackedObject, currentObjectValue?.definitionId, currentObjectValue?.kind]);

  // Create snapshot TrackedObject asynchronously (slow, deferred)
  useEffect(() => {
    if (!referenceMetadata) {
      setReferencedObjectSnapshot(null);
      return;
    }

    // Use setTimeout to defer this work and not block rendering
    const timeoutId = setTimeout(() => {
      const { originalTrackedObject, referenceType, referenceLabel, referenceId } = referenceMetadata;

      const originalValueSnapshot = originalTrackedObject.read();
      const originalObjectId = originalTrackedObject.id;

      const node = props.schema[originalValueSnapshot.kind];
      if (!node) {
        setReferencedObjectSnapshot(null);
        return;
      }

      // Create a new isolated TrackedObject from the snapshot
      const snapshotState = originalTrackedObject.state;
      const snapshotTrackedObject = createFromRawValue(snapshotState, originalValueSnapshot);

      setReferencedObjectSnapshot({
        trackedObject: snapshotTrackedObject,
        node,
        referenceType,
        originalObjectId,
        referenceLabel,
        referenceId
      });
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [referenceMetadata, props.schema]);

  const referencedObjectDisplay = useMemo(() => {
    // Show loading state while snapshot is being created
    if (referenceMetadata && !referencedObjectSnapshot) {
      return (
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md w-full">
          <div className="text-xs text-blue-600 italic">Loading reference preview...</div>
        </div>
      );
    }

    if (!referencedObjectSnapshot) return null;

    const { trackedObject, node, referenceType, originalObjectId, referenceLabel, referenceId } = referencedObjectSnapshot;

    return (
      <div
        key={`ref-display-${props.trackedObject?.id}-${originalObjectId}-${referenceLabel}-${referenceId || ''}`}
        data-reference-display={props.trackedObject?.id}
        className="p-3 bg-blue-50 border border-blue-200 w-full"
      >
        <div className="text-xs font-semibold text-blue-700 mb-2">
          {referenceType} → Definition: {referenceLabel} {referenceId ? `(${referenceId.slice(0, 8)}...)` : ''} (Read-only snapshot)
        </div>
        <div>
          <NodeElement
            node={node}
            schema={props.schema}
            trackedObject={trackedObject}
            path={props.path + '.reference'}
            references={props.references}
            llmContext={props.llmContext}
            currentOutlines={props.currentOutlines}
            getMacro={props.getMacro}
            updateBreadcrumb={() => {}} // No-op for readonly
            displayStacked={props.displayStacked}
            changesToRender={undefined}
            enableKeyIsValid={undefined}
            readonly={true}
          />
        </div>
      </div>
    );
  }, [referenceMetadata, referencedObjectSnapshot, props.trackedObject, props.schema, props.path, props.references, props.llmContext, props.currentOutlines, props.getMacro, props.displayStacked]);
    
  function renderProp (idx: number, p: string, optional: boolean, noLabel?: true) {
    renderedProps[p] = true;

    let showChildChanges = false;
    if (props.changesToRender?.additions?.length || props.changesToRender?.removals?.length) {
      // look down the tree to see if any of the children have had changes applied to them.
      const changeIDs = new Set<string>([...(props.changesToRender?.additions || []).map(a => a.object.id), ...(props.changesToRender?.removals || []).map(r => r.object.id)]);
      const childHasChanges = (obj?: TrackedObject) => { 
        if (!obj) return false;
        if (changeIDs.has(obj.id)) {
          return true;
        }
        if (Array.isArray(obj.value)) {
          for (const child of obj.value) {
            if (childHasChanges(child)) {
              return true;
            }
          }
        } else if (typeof obj.value === 'object') {
          for (const child of Object.keys(obj.value)) {
            if (childHasChanges(obj.value[child])) {
              return true;
            }
          }
        }
        return false;
      }
      showChildChanges = childHasChanges(props.trackedObject?.value[p]);
    }

    let initialShow = (props.node.propMeta?.[p]?.collapsible === 'starts-shown' && !(p in shownProps)) || showChildChanges;

    const { onChangeClearNearest } = props.node.propMeta?.[p] || {};
    const onChange = onChangeClearNearest
      // currently used by `message`, `email_message` and `email_subject` on Notification
      // used to clear the `enableKey` when any of these fields are changed, forcing users to reenable
      ? () => {
        function iterateOnChangeClearNearest(trackedObj : TrackedObject) {
          if (trackedObj instanceof TrackedRecord && onChangeClearNearest! in trackedObj.value) {
            trackedObj.value[onChangeClearNearest!].set('');
            setRerender(rerender + 1);
            return;
          }
          else if (trackedObj.parent) {
            iterateOnChangeClearNearest(trackedObj.parent);
          }
        }
        props.onChange?.();
        return iterateOnChangeClearNearest(props.trackedObject!);
      }
      : props.onChange

    function removeProp() {
      removePropNamed(p);
    }

    function showHover(e: React.MouseEvent<HTMLElement>) {
      const tooltip = e.currentTarget.querySelector('.distroTooltip') as HTMLElement;
      tooltip!.style.display = '';
    }

    function hideHover(e: React.MouseEvent<HTMLElement>) {
      const tooltip = e.currentTarget.querySelector('.distroTooltip') as HTMLElement;
      tooltip!.style.display = 'none';
    }

    async function AskQuestion() {
      const question = window.prompt('What would you like to ask?');
      if (!question) return;
      const response = await (window as any).magic({
        model: chatModel,
        message: `${props.llmContext || ''}The following is a conversation between a human and a helpful AI assistant. The assistant is helpful and concise. The human is writing a JSON-based configuration file. The human has a question about a particular field named ${p}. The field has a value of: ${JSON.stringify(props.trackedObject!.value[p].read())}.

                Human: ${question}
                AI:`,
        max_tokens: 1000,
        top_p: 1,
        frequency_penalty: 0,
        presence_penalty: 0,
        stop: "\n\n",
        temperature: 0,
        question
      });
      if (response?.choices?.length > 0) {
        alert(response.choices[0].message.content);
      } else {
        alert('Uh oh, had trouble ')
      }
    }

    async function Edit() {
      let source = generateSource(props.node.props[p], props.schema);
      source = source.replace(/\n+/g, '\n');
      const qualifier = getTypeQualifier(props.node.props[p], props.schema);

      const question = window.prompt('What would you like to change?');
      const prompt = `${props.llmContext || ''}The following is a collaboration between a human and a helpful AI assistant. 

        The assistant only writes code with typescript types that are explicitly defined below:

        ` + '```\n\n' + source + '```\n\n' + `

        The human has a previously defined object of type ${qualifier}:

        ` + '```\n\n' + JSON.stringify(props.trackedObject!.value[p].read()) + '```\n\n' + `

        They would like you to make the following change: ${question}

        Your edited object:\n\n` + '```';
      console.log(prompt)

      const response = await (window as any).magic({
        model: chatModel,
        message: prompt,
        max_tokens: 1000,
        top_p: 1,
        frequency_penalty: 0,
        presence_penalty: 0,
        stop: "```",
        temperature: 0,
        qualifier,
        question
      });
      if (response?.choices?.length > 0) {
        const result = response.choices[0].message.content.replace('Field Equals', 'Equals');
        const toPaste = JSON.parse(result);
        if (!checkRawMatches(props.node.props[p], props.schema, toPaste)) {
          toast({
            description: "The AI has failed us!",
            variant: 'error'
          });
          return;
        }
        const newElement = createFromRawValue(props.trackedObject!.state, toPaste);
        props.trackedObject?.remove(p);
        props.trackedObject?.set(p, newElement);
      } else {
        toast({
          description: "Uh oh, had trouble",
          variant: 'error'
        });
      }
    }

    async function Copy() {
      const value = props.trackedObject!.value[p].read();
      copyToClipboard(JSON.stringify(value));
      toast({ description: 'Copied to clipboard', variant: 'success' })
    }

    async function Paste() {
      const pastedText = await navigator.clipboard.readText();
      if (pastedText) {
        try {
          const toPaste = JSON.parse(pastedText);
          if (!checkRawMatches(props.node.props[p], props.schema, toPaste)) {
            toast({
              description: "That doesn't belong here!",
              variant: 'error'
            });
            return;
          }
          const newElement = createFromRawValue(props.trackedObject!.state, toPaste, undefined, true);
          props.trackedObject?.remove(p);
          props.trackedObject?.set(p, newElement);
          newElement.locateUserHere();
        } catch (e) {
          console.log('error:', e)
          toast({
            description: "Something went wrong, please try copying again",
            variant: 'error'
          });
        }
      }
    }
        
    const hidden = props.node.propMeta?.[p]?.hidden === 'yes';
    if (hidden) {
      return null;
    }

    const name = props.node.propMeta?.[p]?.name ?? p;
    const commentToggle = props.node.propMeta?.[p]?.comment && <div
      title="Show help text"
      onClick={e => setShowComment({ ...showComment, [p]: !showComment[p] })}
      style={{display: 'block'}}
      className="cursor-pointer"
    >
      &#9432;
    </div>;

    const comment = showComment[p] &&
    <pre className='w-full font-sans text-xs text-gray-700 whitespace-pre-line' title={(props.node.propMeta?.[p]?.comment as any)?.replaceAll('\n', ' ')} >
      {props.node.propMeta?.[p]?.comment}
    </pre>

    const addButton = props.node.propMeta?.[p]?.collapsible &&
                            <button id="expander" data-btn-type={name} onClick={() => {
                              let next = {...shownProps};
                              next[p] = initialShow ? false : !next[p];
                              setShownProps(next);
                            }}>[{initialShow || shownProps[p] ? 'hide' : 'show'}]</button>;
    //value={props.value[p].value} 
    // console.log("Node: ", name, "Props: ", props.node.props[p], "Original meta:", props.node.propMeta[p], ",schema:", props.schema);

    function fieldsMatchAcrossLanguages() {
      const textObj: any = props.trackedObject?.read();
      delete textObj['_id']; // Irrelevant for our purposes

      // Only check if there are more than one languages
      if (Object.keys(textObj).length > 1) {
        const fieldsInLangMap: Record<string, string[]> = {};

        // Collect all referenced target fields for each language
        Object.keys(textObj).forEach((lang) => {
          fieldsInLangMap[lang] = ((textObj[lang] as string).match(/\$[a-zA-Z\\_][a-zA-Z\\_0-9]+/g) || []) as string[];
        });

        for (const lang of Object.keys(fieldsInLangMap)) {
          if (lang === 'en') continue;
          if (fieldsInLangMap[lang]?.length !== fieldsInLangMap['en']?.length) {
            return false;
          }
          for (const field of fieldsInLangMap[lang]) {
            if (!(fieldsInLangMap['en'] || []).includes(field)) {
              return false;
            }
          }
        }
      }

      return true;
    }

    // This little addition lets us visually show in the editor whether or not an input is valid.
    let objectInvalid = false;
    let objectInvalidReason = '';
    let objectInvalidSeverity: keyof typeof MESSAGE_COLOR_MAP = 'error';
    // This conditional covers cases where we want to inspect an object directly
    if ('validateInput' in (props.node.meta || {})) {
      switch (props.node.meta?.['validateInput']) {
        case 'Text':
        case 'RichText':
          if (!fieldsMatchAcrossLanguages()) {
            objectInvalid = true;
            objectInvalidReason = 'Warning: Target fields differ for different languages';
            objectInvalidSeverity = 'warn';
          }
          break;
      }
    } else if ('validateInput' in props.node.props[p].meta!) {
      // This conditional covers cases where we want to inspect a property of the object
      switch (props.node.props[p]?.meta?.['validateInput']) {
        case 'NotificationEnableKey':
          const notifObj: any = props.trackedObject?.read();
          props.node.props[p].meta!['inputValid'] = !!notifObj && props.enableKeyIsValid?.(notifObj.enableKey, notifObj, { allowVersions: ['legacy'] });
          break;
        case 'NotificationFollowupEnableKey':
          const followupObj: any = props.trackedObject?.read();
          const parentNotif: any = props.trackedObject?.parent?.parent?.read();
          if (!parentNotif || !followupObj) {
            props.node.props[p].meta!['inputValid'] = false;
            break;
          }
          // We don't have a real kind for followups, so we augment the object with one.
          const augmentedFollowup = { ...followupObj, kind: 'Notification Followup', targetPrefix: parentNotif.targetPrefix };
          props.node.props[p].meta!['inputValid'] = props.enableKeyIsValid?.(augmentedFollowup.enableKey, augmentedFollowup);
          break;
        case 'PaymentEnableKey': {
          const obj: any = props.trackedObject?.read();
          props.node.props[p].meta!['inputValid'] = !!obj && props.enableKeyIsValid?.(obj.enableKey, obj);
          break;
        }
        case 'AMHEnableKey':
          const amhObj: any = props.trackedObject?.read();
          const amhHash = hash(JSON.stringify({ ...amhObj, enableKey: '' }));
          props.node.props[p].meta!['inputValid'] = !!amhObj && amhObj.enableKey === amhHash;
          break;
        case 'ShouldEncrypt':
          const textObj: any = props.trackedObject?.read();
          if (!textObj) {
            objectInvalid = true;
            objectInvalidReason = 'Warning: TrackedObject is undefined';
            objectInvalidSeverity = 'warn';
            break;
          }
          const encryptedFields = ['bank_account_number', 'ssn', 'itin'];
          const encryptedFieldsRegex = new RegExp(encryptedFields.join('|'), 'i');
          const encrypted = textObj['encrypted'];
          const format = textObj['format'];
          if (!encrypted && !encryptedFields.includes(format) && textObj['targetField'] && encryptedFieldsRegex.test(textObj['targetField'])) {
            objectInvalid = true;
            objectInvalidReason = 'Warning: Sensitive data detected. Please use correct formatting for this field.';
            objectInvalidSeverity = 'warn';
          }
          break;
      }
    }

    const node = <NodeElement node={props.node.props[p]} schema={props.schema}
      onChange={onChange}
      trackedObject={objectToRender[p]}
      path={props.path + ':' + idx.toString()}
      references={props.references}
      llmContext={props.llmContext}
      currentOutlines={props.currentOutlines}
      getMacro={props.getMacro}
      updateBreadcrumb={props.updateBreadcrumb}
      displayStacked={props.displayStacked}
      changesToRender={props.changesToRender}
      enableKeyIsValid={props.enableKeyIsValid}
      readonly={props.readonly}
    />;

    // Note: if this is undefined, the data attribute will automatically not be set on the HTML element
    const valueIfPrimitive = objectToRender[p] instanceof TrackedPrimitive
      ? (String(objectToRender[p].value) ?? '').substring(0, DATA_ATTRIBUTE_SUBSTRING_LENGTH)
      : undefined;

    const background = props.node.propMeta?.[p]?.background;
    const backgroundClass = background ? `bg-${background}` : '';
    
    return <React.Fragment key={p}>
      <div data-path={props.path} {...{[toDataName(p)]: valueIfPrimitive}} className={props.node.propMeta?.[p]?.width ? width[props.node.propMeta[p]?.width!] : ('' + (resolve(props.node.props[p], props.schema)?.kind === 'array' ? "min-w-full" : innerStyle))}>
        <div className={`${props.node.props[p]?.kind === 'string' ? '' : 'border' } p-1 h-full ${backgroundClass}`}>
          <div className={`${props.node.propMeta?.[p]?.collapsible ? 'flex-col' : 'flex'}`}>
            {!noLabel && <>
              <div className={" text-xs py-1 px-2 mt-0.5 mr-2 h-fit inline-block text-gray-500 relative"}>
                <span>
                  <span
                    onClick={removeProp}
                    onMouseOver={showHover}
                    onMouseLeave={hideHover}
                    className={'relative ' + ((props.node.required.includes(p) ? '' : 'italic hover:line-through cursor-pointer'))}>
                    {name}
                    <span className="distroTooltip bg-gray-100 p-1 absolute border border-gray-200 rounded-lg shadow-lg" style={{display: 'none', top: '13px', left: '-3px', zIndex: '1000', width: '120px'}}>
                      <div style={{ border: '6px solid transparent', borderBottom: '6px solid rgb(243 244 246)', top: '-9px', left: '1px'}} className="w-0 h-0 absolute"></div>
                      <div className="hidden pl-1 mt-2 mr-2" style={{'verticalAlign': 'top'}}>AI:</div>
                      <div onClick={(e) => {
                        e.stopPropagation();
                        AskQuestion();
                      }}>
                        <div className="inline-flex items-center cursor-pointer rounded-full border mt-1 border-gray-200 z-50 text-center bg-white text-gray-350 hover:text-blue-700 hover:bg-blue-500" style={{ padding: '1px 6px'}}>
                          <div className="h-6 w-6">
                            <QuestionMarkCircleIcon />
                          </div>
                          <div className="ml-2 mr-2">
                            Ask AI
                          </div>
                        </div>
                      </div>
                      <div onClick={(e) => {
                        e.stopPropagation();
                        Edit();
                      }}>
                        <div className="inline-flex items-center cursor-pointer rounded-full border mt-1 border-gray-200 z-50 text-center bg-white text-gray-350 hover:text-blue-700 hover:bg-blue-500" style={{ padding: '1px 6px'}}>
                          <div className="h-6 w-6">
                            <PencilIcon />
                          </div>
                          <div className="ml-2 mr-2">
                            Edit
                          </div>
                        </div>
                      </div>
                      <div onClick={(e) => {
                        e.stopPropagation();
                        Copy();
                      }}>
                        <div className="inline-flex items-center cursor-pointer rounded-full border mt-1 border-gray-200 z-50 text-center bg-white text-gray-350 hover:text-blue-700 hover:bg-blue-500" style={{ padding: '1px 6px'}}>
                          <div className="h-6 w-6">
                            <Square2StackIcon />
                          </div>
                          <div className="ml-2 mr-2">
                            Copy
                          </div>
                        </div>
                      </div>
                      <div onClick={(e) => {
                        e.stopPropagation();
                        Paste();
                      }}>
                        <div className="inline-flex items-center cursor-pointer rounded-full border mt-1 border-gray-200 z-50 text-center bg-white text-gray-350 hover:text-blue-700 hover:bg-blue-500" style={{ padding: '1px 6px'}}>
                          <div className="h-6 w-6">
                            <ArrowRightCircleIcon />
                          </div>
                          <div className="ml-2 mr-2">
                            Paste
                          </div>
                        </div>
                      </div>
                    </span>
                  </span>
                  {commentToggle}
                </span>
                {addButton}
              </div>
            </>}
            {(props.node.propMeta?.[p]?.collapsible === undefined || shownProps[p] === true || initialShow) && (
              <div className={((props.node.propMeta?.[p]?.collapsible === undefined || shownProps[p] === true || initialShow) ? '' : 'hidden') + ' w-full'}>
                {node}
                {objectInvalid && <div className={`ml-2 ${MESSAGE_COLOR_MAP[objectInvalidSeverity]} text-xs`}>{objectInvalidReason}</div>}
              </div>
            )}
          </div>
          {comment}
        </div>
      </div>
    </React.Fragment>
  }

  // This sets the top-level selected tab for the breadcrumb on initial render before a user selects a tab themselves
  useEffect(() => {
    const initialSelectedProp = Object.keys(props.trackedObject!.value!)[0];
    updateBreadcrumbIfRoot(props.node.propMeta?.[initialSelectedProp]?.name ?? initialSelectedProp);
  }, [])

  function updateBreadcrumbIfRoot(selection: string) {
    // This sets the first element of the breadcrumb based on which property of root is selected
    if (props.trackedObject?.id === 'root') {
      props.updateBreadcrumb(undefined, selection);
    }
  }

  const topProps: string[] = [];
  const bottomProps: string[] = [];
  const allPropKeys = [...props.node.required, ...Object.keys(objectToRender).filter(p => !props.node.required.includes(p) && props.node.props[p] !== undefined)];
  
  for (const p of allPropKeys) {
    const placement = props.node.propMeta?.[p]?.placement;
    if (placement === 'top') {
      if (!topProps.includes(p)) {
        topProps.push(p);
      }
    } else if (placement === 'bottom') {
      if (!bottomProps.includes(p)) {
        bottomProps.push(p);
      }
    }
  }
  
  const topPropsRendered = topProps.map((p, i) => renderProp(i, p, !props.node.required.includes(p)));
  
  const requiredProps = props.node.required.filter(p => !topProps.includes(p) && !bottomProps.includes(p));
  const required = requiredProps.map((p, i) => (renderProp(i + topPropsRendered.length, p, false)));
  
  const optionalProps = Object.keys(objectToRender).filter((p) => !topProps.includes(p) && !bottomProps.includes(p) && !renderedProps[p] && props.node.props[p] !== undefined);
  const optional = optionalProps.map((p, i) => (renderProp(i + topPropsRendered.length + required.length, p, true)));
  
  const bottomPropsRendered = bottomProps.map((p, i) => renderProp(i + topPropsRendered.length + required.length + optional.length, p, !props.node.required.includes(p)));
  const unusedOptional = Object.keys(props.node.props).filter((p) => props.node.propMeta?.[p]?.hidden !== 'yes' && !renderedProps[p])
    .map((p, i) => ({
      name: props.node.propMeta?.[p]?.name ?? p,
      var: p
    }));

  if (style === 'tabs') {
    return <div className='block -mt-1'>
      {props.node.required.filter(p => props.node.propMeta?.[p]?.hidden !== 'yes').map((p) => {
        return <button 
          key={p}
          onClick={() => {
            setSelectedProp(p);
            updateBreadcrumbIfRoot(props.node.propMeta?.[p]?.name ?? p);
          }}
          data-tab-name={p}
          className={'border-b-2 inline-block w-auto m-1 ' + (p === selectedProp ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent')}>{
            props.node.propMeta?.[p]?.name ?? p
          }</button>; 
      })}
      {Object.keys(objectToRender).filter((p) => props.node.propMeta?.[p]?.hidden !== 'yes' && !props.node.required.includes(p) && props.node.props[p] !== undefined).map((p) => {
        return <button 
          key={p}
          onClick={() => {
            setSelectedProp(p);
            updateBreadcrumbIfRoot(props.node.propMeta?.[p]?.name ?? p);
          }}
          data-tab-name={p}
          className={'border-b-2 inline-block w-auto m-1 '
            + (p === selectedProp ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent')
            + (removedProps[p] ? ' bg-red-100' : addedProps[p] ? ' bg-green-100' : '')}>{
            props.node.propMeta?.[p]?.name ?? p
          } {!props.readonly && <span className={'hover:text-red-600'} onClick={(e) => {
            e.stopPropagation();
            removePropNamed(p)
          }}>x</span>}</button>; 
      })}
      {!props.readonly && unusedOptional.length ? <div className="border-b-2 border-transparent inline-block w-auto m-1">
        <Dropdown title="+" options={unusedOptional.map(o => {
          return {name: o.name, callback: () => {
            addPropNamed(o.var);
            setSelectedProp(o.var);
          }};
        })} /></div> : ''}
      <div>
        {renderProp(0, selectedProp, props.node.required.includes(selectedProp), true)}
      </div>
    </div>
  }

  const { renderExtraJsx } = useContext(ExtraJsxContext);
  let extraJsx: React.ReactNode | null = null;
  switch (props.node.meta?.distroKind) {
    case 'Computed':
      extraJsx = renderExtraJsx?.({ action: 'COMPUTED', targetField: props.trackedObject?.read()?.targetField }) ?? null;
      break;
  }

  return <div className={outerStyle}>
    {referencedObjectDisplay && (
      <div className="w-full">
        {referencedObjectDisplay}
      </div>
    )}
    {topPropsRendered}
    {required}
    {optional}
    {bottomPropsRendered}
    {!props.readonly && unusedOptional.length ? <Dropdown title="[..]" className="w-full" options={unusedOptional.map(o => {
      return {name: o.name, callback: () => {
        addPropNamed(o.var);
      }}
    })} /> : ''}
    {extraJsx}
  </div>
}

function ArrayElement(props: {
  node: Node & { kind: 'array' },
  schema: Schema,
  //value: any,
  onChange?: (v?:any) => void,
  setValue: (value: any) => void,
  trackedObject: TrackedArray,
  path: string,
  references?: Record<string, string[]>,
  llmContext?: string,
  currentOutlines?: string[],
  getMacro?: GetMacro,
  displayStacked?: boolean,
  changesToRender?: ChangesToRender,
  updateBreadcrumb: (trackedObj?: TrackedObject) => void,
  enableKeyIsValid?: EnableKeyIsValidFn,
  readonly?: boolean
}) {
  const [arranging, setArranging] = useState(false);
  const pastebuffer = useContext(PastebufferContext);
  const objectShape = useMemo(() => getObjectShape(props.node.element), []);
  const selecting = pastebuffer.shape !== null && pastebuffer.shape === objectShape;
  const [rerender, setRerender] = useState(0);
  const { toast } = useToast();

  function addNewElement(index: number) {
    const prototype = createPrototype(props.node.element, props.schema);
    const newElement = createFromRawValue(props.trackedObject.state, prototype);

    // this field will let us know that the element was just freshly added to the form.
    // other components will set newlyAdded to false once they're done with that info.
    newElement.newlyAdded = true;

    props.trackedObject.insert(index, newElement);
    newElement.locateUserHere();
    setRerender(rerender + 1);
    props.onChange?.();
  }
    
  function removeElement(index: number) {
    //const next = [...(props.value as any[]).slice(0, index), ...(props.value as any[]).slice(index + 1)];
    //props.setValue(next);
    const confirmed = window.confirm('Are you sure you want to delete this?');
    if (confirmed) {
      pastebuffer.removeFromBuffer(props.trackedObject.value[index].id, objectShape);
      props.trackedObject.remove(index);
      setRerender(rerender + 1);
      props.onChange?.();
    }
  }

  async function pasteFromJSON(index: number, json: any) {
    try {
      const toPaste = json.reverse();
      for (let item of toPaste) {
        if (!checkRawMatches(props.node.element, props.schema, item)) {
          toast({
            description: "That doesn't belong here!",
            variant: 'error'
          });
          return;
        }
        const newElement = createFromRawValue(props.trackedObject.state, item, undefined, true);
        props.trackedObject.insert(index, newElement);
        newElement.locateUserHere();
        props.onChange?.();
      }
      setArranging(false);
    } catch (e) {
      toast({
        description: "Something went wrong, please try copying again",
        variant: 'error'
      });
    }
  }

  async function pasteFromClipboard(index: number) {
    const pastedText = await navigator.clipboard.readText();
    if (pastedText && isValidJson(pastedText)) {
      pasteFromJSON(index, JSON.parse(pastedText));
    } else {
      toast({
        description: "Clipboard content is not valid JSON, please try copying again",
        variant: 'error'
      });
    }
  }
  
  async function startMagic(index: number) {
    let source = generateSource(props.node.element, props.schema);
    // Replace needless whitespace
    source = source.replace(/\n+/g, '\n');
    const qualifier = getTypeQualifier(props.node.element, props.schema);
    const question = window.prompt('What do you wish to create?');
    if (!question) return;
    const prompt = source + `

type Distinct<T> = T;

/**
 * @referencedIn fields
 */
export type TargetField = Distinct<string>

/**
 * @references fields
 */
export type TargetFieldRef = Distinct<string>

# A JSON object that contains ${question}

const obj: ${qualifier} =`;
        
    const response = await (window as any).magic({
      model: chatModel,
      message: prompt,
      max_tokens: 1000,
      top_p: 1,
      frequency_penalty: 0,
      presence_penalty: 0,
      stop: "\n\n",
      temperature: 0,
      qualifier,
      question
    });
    console.log(response);
    try {
      let toInsert = json5.parse(response.choices[0].message.content);
      console.log(toInsert);
      if(!Array.isArray(toInsert)) {
        toInsert = [toInsert];
      }
      for(let i = 0; i < toInsert.length; i++) {
        const newElement = createFromRawValue(props.trackedObject.state, toInsert[i]);
        console.log(newElement.read())
        props.trackedObject.insert(index + i, newElement);
        newElement.locateUserHere();
        setRerender(rerender + 1);
      }
    } catch (e) {
      alert(e);
    }
  }
    
  // Helper to render the icons that control adding a new element, moving an elemnt, copy paste, etc.
  function dot(index: number, emptyList?: boolean) {
    return <div className={`pl-1 ${emptyList ? 'mt-2 h-6' : 'h-0'} ${emptyList || selecting || arranging ? 'flex' : 'hidden'}`}>
      {/* The absolute div below extends the button row hover area beyond the individual buttons to make the buttons easier to click */}
      {/* The div size is optimized for 3 small buttons */}
      <div className="relative">
        <div className="absolute w-16 h-12" style={{ top: '-18px', left: '1.5rem' }} />
      </div>

      {/* move to icon (-->) - show this only if pastebuffer.shape exists, ie there is something to paste */}
      {pastebuffer.shape && 
                <div onClick={() => {relocate(index)}} className={"shadow-lg cursor-pointer rounded-full -mt-2 border-white h-7 w-7 z-50 text-center " + 
                    'bg-gray-100 text-gray-300 hover:text-blue-700 hover:bg-blue-500'} >{'→'}</div>
      }

      {/* duplicate element */}
      {selecting &&
                <>
                  <div onClick={() => { copyPaste(index)}} className="shadow-lg cursor-pointer rounded-full -mt-2 border-white h-7 w-7 z-50 text-center bg-gray-100 text-gray-300 hover:text-blue-700 hover:bg-blue-500" style={{ padding: '3px' }}>
                    <Square2StackIcon />
                  </div>
                </>
      }

      {/* Plus, sparkles, and paste */}
      {!selecting &&
        <>
          <div onClick={() => addNewElement(index)} className={"shadow-lg cursor-pointer rounded-full -mt-2 border-white h-7 w-7 z-50 text-center " + 
            'bg-gray-100 text-gray-300 hover:text-blue-700 hover:bg-blue-500'} >+</div>
          <div onClick={() => startMagic(index)} className="shadow-lg cursor-pointer rounded-full -mt-2 border-white h-7 w-7 z-50 text-center bg-gray-100 text-gray-300 hover:text-blue-700 hover:bg-blue-500" style={{ padding: '3px' }}>
            <SparklesIcon />
          </div>
          <div className="shadow-xl cursor-pointer rounded-full flex items-center justify-center -mt-2 border-white h-7 w-7 z-50 bg-gray-100 text-gray-300 hover:text-blue-700 hover:bg-blue-500" style={{ padding: '3px' }} onClick={() => pasteFromClipboard(index)}>
            <ClipboardDocumentCheckIcon className="w-6 h-6" />
          </div>
          {props.trackedObject?.value?.[index]?.value?.kind?.value === 'Macro' &&
            <div 
              onClick={async () => {
                if (props.getMacro) {
                  const macroResponse = await props.getMacro(props.trackedObject?.value?.[index]?.read())
                  if (macroResponse) {
                    console.log('Expanded Macro, attempting to paste', macroResponse)
                    pasteFromJSON(index + 1, macroResponse)
                    setRerender(rerender + 1);
                  }
                } else {
                  alert('Cannot expand macro here')
                }}}
              className="shadow-lg cursor-pointer rounded-full -mt-2 border-white h-7 w-7 z-50 text-center bg-gray-100 text-gray-300 hover:text-blue-700 hover:bg-blue-500" 
              style={{ padding: '3px' }}
            >
              <ArrowsPointingOutIcon />
            </div>}
        </>
      }
    </div>
  }
    
  function relocate(index: number) {
    // Figure out the sort key where we want to append after
    const sortKey = props.trackedObject.value[index]?.sortKey!;

    // first determine if the node we are trying to move to actually matches in the trackedObject(s) we are moving
    let newNode = props.node.element;
    let toAdd = pastebuffer.slurp(false);
    let canAdd = true;
    for (let i = 0; i < toAdd.length; i++) {
      if (!checkMatches(newNode, props.schema, toAdd[i]!)) {
        canAdd = false;
        break;
      }
    }

    if (!canAdd) {
      toast({
        description: "Something doesn't belong here!",
        variant: 'error'
      });
      return;
    }

    // now that we are sure we can move the new object(s) over, we can 
    // remove them from the pastebuffer and from their original slots
    toAdd = pastebuffer.slurp(true);
    toAdd.reverse();

    let newIndex = 0;
    if (!sortKey) {
      newIndex = props.trackedObject.value.length;
    } else {
      for (var i = 0; i < props.trackedObject.value.length; i++) {
        if (SortFunction(sortKey, props.trackedObject.value[i].sortKey!) > 0) {
          newIndex += 1;
        }
      }
    }

    // Add the selected elements in reverse order
    for (let i = 0; i < toAdd.length; i++) {
      props.trackedObject.insert(newIndex, toAdd[i]);

      // TODO anne: this only works if we're relocating one thing. However, relocating multiple things seems 
      // broken anyway. So we should revisit this eventually.
      toAdd[i].locateUserHere();
    }

    // Rerender
    setRerender(rerender + 1);

    setArranging(false);
  }

  function copyPaste(index: number) {
    const toAdd = pastebuffer.slurp(false);
    toAdd.reverse();

    for (let bufferItem of toAdd) {
      const newElement = createFromRawValue(props.trackedObject.state, bufferItem.read(), undefined, true);
      props.trackedObject.insert(index, newElement);
      newElement.locateUserHere();
    }

    setRerender(rerender + 1);
  }

  const removedElements: Set<string> = new Set();
  const addedElements: Set<string> = new Set();

  // If there are removals in our changesToRender that belong to this object, we want to render them.
  let objectToRender = [...props.trackedObject?.value];
  for (const removal of props.changesToRender?.removals || []) {
    if (removal.location?.kind === 'array' && removal.location.id === props.trackedObject?.id) {
      const index = objectToRender.filter((v) => SortFunction(v.sortKey!, (removal.location as any).sortKey as SortKey) < 0).length;
      objectToRender = [...objectToRender.slice(0, index), removal.object, ...objectToRender.slice(index)];
      removedElements.add(removal.object.id);
    }
  }
  for (const addition of props.changesToRender?.additions || []) {
    if (addition.location?.kind === 'array' && addition.location.id === props.trackedObject?.id) {
      addedElements.add(addition.object.id);
    }
  }
    
  // {getDescription(props.node, props.schema)}
  return <div className="w-full group"
    onMouseLeave={() => {
      setArranging(false);
    }}>
    {!props.readonly && dot(0, objectToRender.length === 0)}
    {objectToRender.map((v, i) => {
      const selected = !!(pastebuffer.buffer || {})[v.id];
      return <div key={v.id} className="w-full">
        <div className="mt-1 pl-1 flex">
          {!props.readonly && (
            <>
              <div className={(arranging ? '' : 'hidden') + " w-0 h-0"}>
                <div
                  onClick={() => { removeElement(i) }}
                  className={'-m-6 mt-2 shadow-lg cursor-pointer rounded-l-full border-white p-1 pl-2 h-6 w-6 z-50 text-center text-xs bg-gray-100 text-gray-300 hover:text-red-700 hover:bg-red-500'}>✕</div>
              </div>
              <div className={(selected ? 'bg-blue-400 hover:bg-blue-500 text-blue-700' :
                `bg-gray-100 ${pastebuffer.shape && pastebuffer.shape !== objectShape ? '' : 'hover:bg-gray-300'} text-gray-300`) +
                        " w-6 flex-none cursor-pointer p-2 text-gray-300"}
              onClick={() => {
                if (objectToRender?.length > props.trackedObject.value.length) {
                  return;
                }
                if (pastebuffer.shape && pastebuffer.shape !== objectShape) {
                  return;
                }
                if (selected) {
                  pastebuffer.removeFromBuffer(props.trackedObject.value[i].id, objectShape);
                } else {
                  const object = props.trackedObject.value[i];
                  pastebuffer.addToBuffer(
                    props.trackedObject.value[i].id,
                    objectShape,
                    () => [...props.path.split(':').filter(i => i).map((i) => parseInt(i)), i],
                    (remove) => {
                      const toReturn = object; //props.trackedObject.value[i];
                      if (remove) {
                        props.trackedObject.remove(props.trackedObject.value.indexOf(object));
                      }

                      pastebuffer.removeFromBuffer(object.id, objectShape);

                      return toReturn;
                    });
                }
              }}
              onMouseEnter={() => {
                setArranging(true);
              }}
              >{i}</div>
            </>
          )}

          <div
            className="flex-grow"
            onMouseEnter={() => {
              setArranging(false);
            }}>
            <NodeElement node={props.node.element} schema={props.schema}
              onChange={props.onChange}
              trackedObject={objectToRender[i]}
              path={props.path + ':' + i.toString()}
              references={props.references}
              llmContext={props.llmContext}
              currentOutlines={props.currentOutlines}
              getMacro={props.getMacro}
              updateBreadcrumb={props.updateBreadcrumb}
              displayStacked={props.displayStacked}
              changesToRender={props.changesToRender}
              enableKeyIsValid={props.enableKeyIsValid}
              readonly={props.readonly}
            />
          </div>
        </div>
        {!props.readonly && dot(i + 1)}
      </div>}
    )}
  </div>
}

function createPrototype(node: Node, schema: Schema): any {
  switch(node.kind) {
    case 'object': {
      const prototype = node.required.reduce((acc, p) => {
        acc[p] = createPrototype(node.props[p], schema);
        return acc;
      }, {} as Record<string, any>);

      // This covers non-required IDs, allowing us to add an id prop
      // to existing types as an optional, which saves us having
      // to write a migration each time
      Object.entries(node.props)
        .filter(([_, val]) => val.meta?.uuid)
        .forEach(([key, _]) => {
          prototype[key] ||= uuidv4();
        });
      return prototype;
    }
    case 'array': {
      return []
    }
    case 'ref': {
      return createPrototype(schema[node.label], schema);
    }
    case 'string': {
      if ((node.meta || {}).uuid) {
        return uuidv4();
      }
      return '';
    }
    case 'boolean': {
      return false;
    }
    case 'number': {
      return 0;
    }
    case 'literal': {
      return node.value;
    }
    case 'union': {
      return createPrototype(node.options[0], schema);
    }
    case 'intersection': {
      const toMerge = node.constraints.map(c => createPrototype(c, schema));
      return toMerge.reduce((acc, p) => {
        return {
          ...acc,
          ...p
        };
      }, {});
    }
    default: {
      return NaN;
    }
  }
}

function getDescription(node: Node, schema: Schema): string {
  if (node.name) return node.name;
  switch(node.kind) {
    case 'object': {
      if (node.props.kind?.kind === 'literal' && typeof node.props.kind.value === 'string') {
        return node.props.kind.value;
      }
      return "[An object with " + node.required.join(", ") + " properties]";
    }
    case 'array': {
      return "A List of " + getDescription(node.element, schema);
    }
    case 'ref': {
      return node.label;
    }
    case 'boolean': {
      return "boolean";
    }
    case 'string': {
      return "string";
    }
    case 'number': {
      return "number";
    }
    case 'literal': {
      return node.value;
    }
    case 'union': {
      return "One of " + node.options.map(o => getDescription(o, schema)).join(", ");
    }
    case 'intersection': {
      if (node.meta?.name) {
        return node.meta.name;
      }
      if (schema.Block?.meta?.['intersection-naming'] === 'first-only') {
        return getDescription(node.constraints[0], schema);
      }
      return node.constraints.map(c => getDescription(c, schema)).join(" + ");
    }
    default: {
      console.log("Unknown node kind: ", node.kind);
      return "Unknown";
    }
  }
}


function getTypeQualifier(node: Node, schema: Schema): string {
  if (node.name) return node.name;
  switch(node.kind) {
    case 'object': {
      return '{' + 
                [node.required.map(p => "'p': " + getTypeQualifier).join(",\n") +
                Object.keys(node.props).filter(p => !node.required.includes(p))
                  .map(p => "'p'?: " + getTypeQualifier).join(",\n")]
                  .filter(p => p).join(',n')
                + '}';
    }
    case 'array': {
      return getTypeQualifier(node.element, schema) + '[]';
    }
    case 'ref': {
      return node.label;
    }
    case 'boolean': {
      return "boolean";
    }
    case 'string': {
      return "string";
    }
    case 'number': {
      return "number";
    }
    case 'literal': {
      return "'" + node.value + "'";
    }
    case 'union': {
      return node.options.map(o => getTypeQualifier(o, schema)).join(" | ");
    }
    case 'intersection': {
      return node.constraints.map(o => getTypeQualifier(o, schema)).join(" & ");
    }
    default: {
      return "never";
    }
  }
}


function generateSource(node: Node, schema: Schema, found?: Record<string, true>): string {
  let toReturn = '';
  found = found || {};
  switch(node.kind) {
    case 'object': {
      return node.required.map((p) => generateSource(node.props[p], schema, found)).filter(s => s).join('\n');
    }
    case 'array': {
      return generateSource(node.element, schema, found);
    }
    case 'ref': {
      if (!found[node.label]) {
        found[node.label] = true;
        return (node.source || '') + '\n' + generateSource(schema[node.label], schema, found);
      }
      return '';
    }
    case 'string': {
      return '';
    }
    case 'boolean': {
      return '';
    }
    case 'number': {
      return '';
    }
    case 'literal': {
      return '';
    }
    case 'union': {
      return node.options.map(o => generateSource(o, schema, found)).filter(s => s).join('\n');
    }
    case 'intersection': {
      return node.constraints.map(o => generateSource(o, schema, found)).filter(s => s).join('\n');
    }
    default: {
      return '';
    }
  }
}

function stringHash(str: string) {
  var hash = 0, i, chr;
  if (str.length === 0) return hash;
  for (i = 0; i < str.length; i++) {
    chr   = str.charCodeAt(i);
    hash  = ((hash << 5) - hash) + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

let shapeCache = new WeakMap();
function getObjectShape(node: Node): string {
  if (shapeCache.has(node)) {
    return shapeCache.get(node);    
  } else {
    shapeCache.set(node, stringHash(JSON.stringify(node)));
    console.log(shapeCache)
    return shapeCache.get(node);    
  }
}

function resolve(node: Node, schema: Schema): Node {
  if (node?.kind === 'ref') {
    return resolve(schema[node.label], schema);
  }
  return node;
}

interface BranchDiagnostic {
  matched: boolean;
  depth: number;
  failurePoint?: {
    path: string;
    reason: string;
    expected: string;
    actual: string;
  };
}

function getMatchDiagnostics(node: Node, schema: Schema, obj: TrackedObject, pathPrefix: string = ''): BranchDiagnostic {
  let maxDepth = 0;
  let failurePoint: BranchDiagnostic['failurePoint'] | undefined;

  function checkNodeBreadthFirst(n: Node, o: TrackedObject | undefined, path: string, currentDepth: number): boolean {
    maxDepth = Math.max(maxDepth, currentDepth);

    if (!o) {
      if (!failurePoint || currentDepth > maxDepth) {
        failurePoint = {
          path,
          reason: 'Missing or undefined',
          expected: getDescription(n, schema),
          actual: 'undefined'
        };
      }
      return false;
    }

    switch(n.kind) {
      case 'object': {
        const missingRequired = n.required.filter(p => o.value[p] === undefined);
        
        if (missingRequired.length > 0) {
          if (!failurePoint) {
            failurePoint = {
              path,
              reason: 'Missing required properties',
              expected: `object with required: [${n.required.join(', ')}]`,
              actual: JSON.stringify(o.read(), null, 2),
            };
          }
          return false;
        }

        for (const [propName, propNode] of Object.entries(n.props)) {
          if (o.value[propName] !== undefined) {
            const propPath = path ? `${path}.${propName}` : propName;
            if (!checkNodeBreadthFirst(propNode, o.value[propName], propPath, currentDepth + 1)) {
              return false;
            }
          }
        }
        return true;
      }

      case 'array': {
        if (!Array.isArray(o.value)) {
          if (!failurePoint || currentDepth >= maxDepth) {
            failurePoint = {
              path,
              reason: 'Type mismatch',
              expected: 'array',
              actual: typeof o.value
            };
          }
          return false;
        }

        for (let idx = 0; idx < (o.value as any[]).length; idx++) {
          const item = (o.value as any[])[idx];
          if (!checkNodeBreadthFirst(n.element, item, `${path}[${idx}]`, currentDepth + 1)) {
            return false;
          }
        }
        return true;
      }

      case 'ref': {
        return checkNodeBreadthFirst(schema[n.label], o, path, currentDepth);
      }

      case 'string': {
        if (typeof o.value !== 'string') {
          return false;
        }
        return true;
      }

      case 'number': {
        if (typeof o.value !== 'number') {
          return false;
        }
        return true;
      }

      case 'boolean': {
        if (typeof o.value !== 'boolean') {
          return false;
        }
        return true;
      }

      case 'literal': {
        if (o.value !== n.value) {
          return false;
        }
        return true;
      }

      case 'union': {
        let deepestUnionDepth = currentDepth;
        let deepestUnionFailure: BranchDiagnostic['failurePoint'] | undefined;
        
        for (const option of n.options) {
          if (checkMatches(option, schema, o)) {
            return true;
          }
          
          const optionDiagnostic = getMatchDiagnostics(option, schema, o, path);
          const absoluteDepth = currentDepth + optionDiagnostic.depth;
          if (absoluteDepth > deepestUnionDepth) {
            deepestUnionDepth = absoluteDepth;
            deepestUnionFailure = optionDiagnostic.failurePoint;
          }
        }
        
        if (deepestUnionFailure && deepestUnionDepth > maxDepth) {
          maxDepth = deepestUnionDepth;
          failurePoint = deepestUnionFailure;
        } else if (!failurePoint || currentDepth >= maxDepth) {
          failurePoint = {
            path,
            reason: 'No union branch matched',
            expected: `union(${n.options.length} options)`,
            actual: JSON.stringify(o.read(), null, 2),
          };
        }
        return false;
      }

      case 'intersection': {
        for (const constraint of n.constraints) {
          if (!checkNodeBreadthFirst(constraint, o, path, currentDepth)) {
            return false;
          }
        }
        return true;
      }

      default: {
        if (!failurePoint || currentDepth >= maxDepth) {
          failurePoint = {
            path,
            reason: 'Unknown node kind',
            expected: (n as any).kind || 'unknown',
            actual: JSON.stringify(o.read(), null, 2)
          };
        }
        return false;
      }
    }
  }

  const matched = checkNodeBreadthFirst(node, obj, pathPrefix, 0);
  
  return { 
    matched, 
    depth: maxDepth,
    failurePoint: matched ? undefined : failurePoint
  };
}

function checkMatches(node: Node, schema: Schema, obj: TrackedObject): boolean {
  if (!obj) return false;

  switch(node.kind) {
    case 'object': {
      // Check that all required properties are present
      if (!node.required.reduce((acc, p) => acc && obj.value[p] !== undefined, true)) {
        return false;
      }

      // Check that all known properties are typed properly
      return Object.keys(obj.value as Record<string, TrackedObject>).reduce((acc: boolean, p) => {
        if (node.props[p]) {
          return acc && checkMatches(node.props[p], schema, obj.value[p])
        }
        return acc;
      }, true)
    }
    case 'array': {
      return Array.isArray(obj.value) && (obj.value as any[]).reduce((acc, v) => acc && checkMatches(node.element, schema, v), true);
    }
    case 'ref': {
      return checkMatches(schema[node.label], schema, obj);
    }
    case 'string': {
      return typeof obj.value === 'string';
    }
    case 'number': {
      return typeof obj.value === 'number';
    }
    case 'boolean': {
      return typeof obj.value === 'boolean';
    }
    case 'literal': {
      return obj.value === node.value;
    }
    case 'union': {
      return node.options.reduce((acc: boolean, v) => {
        return acc || checkMatches(v, schema, obj)
      }, false);
    }
    case 'intersection': {
      return node.constraints.reduce((acc: boolean, v) => {
        return acc && checkMatches(v, schema, obj)
      }, true);
    }
    default: {
      return false;
    }
  }
}

function checkRawMatches(node: Node, schema: Schema, obj: any, exactMatch?: boolean): boolean {
  if (obj === undefined || obj === null) {
    return false;
  }
    
  switch(node.kind) {
    case 'object': {
      // Check that all required properties are present
      if (!node.required.reduce((acc, p) => acc && obj[p] !== undefined, true)) {
        return false;
      }

      // Check that all known properties are typed properly. If exactMatch is true, then
      // the object and the node must have exactly the same properties, no more, no less.
      const objKeys = Object.keys(obj as Record<string, TrackedObject>);
      if (exactMatch && objKeys.length !== Object.keys(node.props).length) {
        return false;
      }
      return objKeys.reduce((acc: boolean, p) => {
        if (node.props[p]) {
          return acc && checkRawMatches(node.props[p], schema, obj[p], exactMatch);
        } else if (exactMatch) {
          return false;
        }
        return acc;
      }, true);
    }
    case 'array': {
      return Array.isArray(obj) && (obj.length === 0 || (obj as any[]).reduce((acc, v) => {
        return acc && checkRawMatches(node.element, schema, v), true}, exactMatch));
    }
    case 'ref': {
      return checkRawMatches(schema[node.label], schema, obj, exactMatch);
    }
    case 'string': {
      return typeof obj === 'string';
    }
    case 'number': {
      return typeof obj === 'number';
    }
    case 'boolean': {
      return typeof obj === 'boolean';
    }
    case 'literal': {
      return obj === node.value;
    }
    case 'union': {
      return node.options.reduce((acc: boolean, v) => {
        return acc || checkRawMatches(v, schema, obj, exactMatch);
      }, false);
    }
    case 'intersection': {
      return node.constraints.reduce((acc: boolean, v) => {
        return acc && checkRawMatches(v, schema, obj, exactMatch);
      }, true);
    }
    default: {
      return false;
    }
  }
}

export function expandMacrosInner(obj: any, schema: Schema, macros: [Node, (_: any) => any][]): any {
  if (Array.isArray(obj)) {
    const toReturn = obj.map((o: any) => expandMacrosInner(o, schema, macros));

    return toReturn.reduce((acc, o) => {
      for (const [node, macro] of macros) {
        if (node.kind === 'object') {
          let match = true;
          for (const p in node.props) {
            if ((node.required.includes(p) && o[p] === undefined) || (o[p] && !checkRawMatches(node.props[p], schema, o[p]))) {
              // console.log("BZN Didn't match", p, node.props[p], o[p])
              match = false;
              break;
            }
          }
          if (match) {
            return acc.concat(macro(o));
          }
        } else {
          throw Error('We only support object macros')
        }
      }
      acc.push(o);
      return acc;
    }, []);
  } else if (typeof obj === 'object' && obj !== null) {
    return Object.keys(obj).reduce((o: any, k: string) => {
      o[k] = expandMacrosInner(obj[k], schema, macros);
      return o;
    }, {});
  }
  return obj;
}

// Return type of this function maps to `ExpandedSurvey` 
export function expandMacros(obj: any, schema: Schema, macros: Record<string, (_: any) => any>): Record<string, any> {
  const pairedMacros = [] as [Node, (_: any) => any][];
  for (const k in schema) {
    const node = schema[k];
    if (node.kind === 'object' && node.expander) {
      pairedMacros.push([node, macros[node.expander]]);
    }
  }
  // console.log("BZN Paired Macros", pairedMacros)
  return expandMacrosInner(obj, schema, pairedMacros);
}

function extractReferences(node: Node, schema: Schema, obj: TrackedObject, references: Record<string, string[]>)  {
  if (!obj || !node) return;

  switch(node.kind) {
    case 'object': {
      // Check that all known properties are typed propertly
      Object.keys(obj.value as Record<string, TrackedObject>).map((p) => {
        if (node.props[p]) {
          extractReferences(node.props[p], schema, obj.value[p], references)
        }
      })
      return;
    }
    case 'array': {
      (obj.value as any[]).map((v) => extractReferences(node.element, schema, v, references));
      return;
    }
    case 'ref': {
      extractReferences(schema[node.label], schema, obj, references);
    }
    case 'string': {
      if (node.meta?.referencedIn) {
        references[node.meta.referencedIn] = references[node.meta.referencedIn] || [];
        references[node.meta.referencedIn].push(obj.value);
      }
      return;
    }
    case 'number': {
      return;
    }
    case 'boolean': {
      return;
    }
    case 'literal': {
      return;
    }
    case 'union': {
      for (const branch of node.options) {
        if (checkMatches(branch, schema, obj)) {
          extractReferences(branch, schema, obj, references);
          break;
        }
      }
      return;
    }
    case 'intersection': {
      for (const part of node.constraints) {
        extractReferences(part, schema, obj, references);
      }
    }
    default: {
      return false;
    }
  }
}

const Editor_: React.ForwardRefRenderFunction<DistroEditorHandle, {
  types: string,
  value: any,
  migrations?: Record<string, (qType: TrackedRecord) => void>,
  macros: Record<string, (qType: any) => void>,
  programConfig?: Record<string, any> | null,
  root: string,
  rerender?: number,
  onChange: (value: any, state: State, events: ChangeSet, expanded: Record<string, any> | null, myActivity?: UserActivity) => void,
  getMacro?: GetMacro,
  macroEditor?: boolean,
  showBreadcrumb?: boolean,
  displayStacked?: boolean,
  renderExtraJsx?: ExtraJsx,
}> = (props, ref) => {
  // The source code that was parsed to get types
  const [source, setSource] = useState(props.types);

  const [initialValue, setInitialValue] = useState();
  const [initialEvents, setInitialEvents] = useState<ChangeSet>([]);
  const [initialUnpublishedEvents, setInitialUnpublishedEvents] = useState<ChangeSet>([]);
  const [initialUserActivity, setInitialUserActivity] = useState<UserActivity[]>([]);
    
  // The Distro interpretation of the types
  const [schema, setSchema] = useState<Schema>({});
  const schemaRef = useRef<Schema | null>(null);
  schemaRef.current = schema;

  // The JSON object we're actually editing (and a ref to it)
  const [config, setConfig] = useState<TrackedObject>(props.value);
  const configRef = useRef<TrackedObject | null>(null);

  const macroRef = useRef<Record<string, (qType: any) => void>>({});
  macroRef.current = props.macros;

  const [references, setReferences] = useState<Record<string, string[]>>({});
  const externalReferences = useRef<Record<string, string[]>>({});

  const [selectedTopLevelTab, setSelectedTopLevelTab] = useState<string>();

  const [llmContext, setLlmContext] = useState<string>('');
  const [author, setAuthor] = useState<Author>();

  const [debug, setDebug] = useState<boolean>(false);
  const [enableKeyIsValid, setEnableKeyIsValid] = useState<EnableKeyIsValidFn | undefined>();
  const [changesToRender, setChangesToRender] = useState<ChangesToRender | undefined>();
  const doExtractReferences = () => {
    if (schemaRef.current && configRef.current) {
      const toSet = {} as Record<string, string[]>;
      extractReferences(schemaRef.current[props.root || 'Root'], schemaRef.current, configRef.current, toSet);
      for (const k in toSet) {
        // Remove duplicates using reduce
        toSet[k] = toSet[k].sort().reduce((acc, v) => {
          if (acc.length === 0 || acc[acc.length - 1] !== v) {
            acc.push(v);
          }
          return acc;
        }, [] as string[]);
      }
      const toMerge = externalReferences.current;
      // Merge from toMerge into toSet and remove any duplicates
      for (const k in toMerge) {
        toSet[k] = (toSet[k] || []).concat(toMerge[k]).sort().reduce((acc, v) => {
          if (acc.length === 0 || acc[acc.length - 1] !== v) {
            acc.push(v);
          }
          return acc;
        }, [] as string[]);
      }
      setReferences(toSet);
    }
  }
    
  // Collab state (read: edit history) for the object we're editing
  const changeCallback = (events: ChangeSet) => {
    if (configRef.current) {
      const value = configRef.current.read();
      const expanded = expandMacros(value, schemaRef.current!, macroRef.current!);

      props.onChange(value, configRef.current.state, events, expanded);
      doExtractReferences();
    }
  };

  // Top-level pastebuffer information
  const [pastebufferValue, setPastebufferValue] = useState<Record<string, [() => number[], (remove: boolean) => TrackedObject]>>({});
  const [pastebufferType, setPastebufferType] = useState<string>();

  // State var to force re-renders
  const [generation, setGeneration] = useState(0);

  // The semi-external API that allows Distro consumers to manage state
  useImperativeHandle(ref, () => ({
    initialize: async (value: any, 
      author?: Author, 
      events?: ChangeSet, 
      userActivity?: UserActivity[], 
      references?: Record<string, string[]>, 
      llmContext?: string, 
      unpublishedEvents?: ChangeSet,
      options?: Parameters<DistroEditorHandle['initialize']>[7]
    ) => {
      externalReferences.current = references || externalReferences.current || {};
      setInitialValue(value);
      setInitialEvents(events || []);
      setInitialUnpublishedEvents(unpublishedEvents || []);
      setInitialUserActivity(userActivity || []);
      setLlmContext(llmContext || '');
      setAuthor(author);
      setDebug(!!options?.debug);
      if (typeof options?.enableKeyIsValid === 'function') {
        setEnableKeyIsValid(() => options.enableKeyIsValid)
      }
      if (options?.changesToRender) {
        setChangesToRender(options.changesToRender);
      }
    },
    update: (value: any) => {

    },
    mergeState: (collabState: State) => {
      //console.log("Merging", Object.keys(collabState.current.objects), Object.keys(e.detail.state.objects))
      //debugIDs(collabState.current);
      //debugIDs(e.detail.state);
      const [root, state] = ReconstructState(config.state, collabState);
      //console.log("Merged", root.read(), state);
      //debugIDs(collabState.current);
      setGeneration((r) => r + 1);
      props.onChange(configRef.current!.read(), configRef.current!.state, [], null);
    },
    mergeEvents: (events: ChangeSet, userActivity?: UserActivity[], unpublishedEvents?: ChangeSet) => {
      if (!events || !config) {
        return;
      }
      mergeStateFromEvents(config.state, events);
      if (userActivity) updateUserActivity(config.state, userActivity);
      if (unpublishedEvents) {
        config.state.unpublishedChanges = (config.state.unpublishedChanges || []).concat(unpublishedEvents);
      }
      setGeneration((r) => r + 1);
      props.onChange(configRef.current!.read(), configRef.current!.state, [], null);
    },
    getPrunedEvents: (state: State) => {
      return []
    }
  }));

  useEffect(() => {
    if (initialValue && Object.keys(schema).length > 0) {
      let obj: TrackedObject;
      if (initialEvents.length > 0) {
        let state: State;
        [obj, state] = reconstructStateFromEvents(initialEvents, debug, changeCallback, true);
        if (initialUserActivity) updateUserActivity(state, initialUserActivity);
      } else {
        const initialState = createInitialState(changeCallback);
        // we're creating our Distro from an initialValue, so we are replaying these events, 
        // and should not treat them like they're new events coming in.
        initialState.replaying = true;
        obj = createFromRawValue(initialState, initialValue, true);
      }

      if (initialUnpublishedEvents.length > 0) {
        obj.state.unpublishedChanges = initialUnpublishedEvents;
      }

      obj.state.replaying = false;
      obj.state.currentAuthor = author!;

      doMigrations(schema[props.root || 'Root'], schema, obj, props.migrations ?? {});
      configRef.current = obj;
      setConfig(obj);

      doExtractReferences();
      props.onChange(configRef.current.read(), configRef.current.state, [], null);
    }
  }, [schema, initialValue, initialEvents, author, debug]);

  // When the source property changes, make sure we rerender by pushing it to state
  // TODO: Do we need this? - it's definitely necessary for collab as of 3/09/2023
  useEffect(() => {
    setSource(props.types);
  }, [props.types]);
   
  // When the source changes, re-parse it
  useEffect(() => {
    if (source?.trim().length > 0 && Object.keys(schema).length == 0) {
      (async () => {
        const macrosEnabled = props.programConfig?.experimental?.macros === true;
        const macroEditor = props.macroEditor
        const parser = new Parser(props.root, {macrosEnabled, macroEditor});
        setSchema((await parser.initWithContent(source))!);
        doExtractReferences();
      })()
    }
  }, [source]);

  const [breadcrumb, setBreadcrumb] = useState<JSX.Element | null>(null);

  // Traces its way back up through the TrackedObject tree, adding steps along the way to the sticky breadcrumb
  // at the top of the editor.
  const updateBreadcrumb = (trackedObj?: TrackedObject, rootSelection?: string) => {
    if (!props.programConfig?.experimental?.distroBreadcrumbs || !props.showBreadcrumb) {
      return; // Do nothing if experiment is off or not toggled
    }

    // This helps us avoid accidentally trying to render an object directly
    const isNonEmptyString = (obj: any) => {
      if (obj && typeof obj === 'string') {
        return obj;
      }
      return '';
    }

    const path = [];
    let trackedObjRef = trackedObj;
    while (trackedObjRef?.parent) {
      if (trackedObjRef?.value?.kind?.value) {
        const breadcrumbTitle = isNonEmptyString(trackedObjRef?.value?.title?.value?.en?.value)
          || isNonEmptyString(trackedObjRef?.value?.title?.value)
          || isNonEmptyString(trackedObjRef?.value?.name?.value?.en?.value)
          || isNonEmptyString(trackedObjRef?.value?.name?.value)
          || isNonEmptyString(trackedObjRef?.value?.targetField?.value)
          || isNonEmptyString(trackedObjRef?.value?.path?.value);

        path.push(
          <span className="inline-flex items-center">
            <span className="rounded-full text-xs px-2 py-1 inline-block mr-1" style={{ "backgroundColor": stringToColour(trackedObjRef?.value?.kind?.value ?? '') }}>
              {trackedObjRef?.value?.kind?.value}
            </span>
            <span className="ml-1 text-gray-700 text-xs">
              {breadcrumbTitle}
            </span>
          </span>
        );

        path.push(<ChevronRightIcon className="w-5 text-gray-400 inline-block" aria-hidden="true" />)
      }
      trackedObjRef = trackedObjRef?.parent;
    }

    if (rootSelection) {
      setSelectedTopLevelTab(rootSelection);
    }
    // Push the current top level tab
    path.push(
      <span className="inline-flex items-center">
        <span className="ml-1 text-gray-700 text-xs">
          {(rootSelection || selectedTopLevelTab)}
        </span>
      </span>
    );

    setBreadcrumb(<span>{...path.reverse()}</span>);
  }

  return <>
    {(props.programConfig?.experimental?.distroBreadcrumbs && props.showBreadcrumb) && (
      // min-h-[x] doesn't work in tailwind version 3.1.8
      <div className="sticky top-0 left-0 w-full z-50 px-2 bg-gray-100 content-center border-b shadow-sm" style={{ 'minHeight': '34px' }}>
        {breadcrumb}
      </div>
    )}
    <div className="px-2">
      <PastebufferContext.Provider value={{
        buffer: pastebufferValue,
        addToBuffer: (id: string, shape: string, locate: () => number[], slurp: (remove: boolean) => TrackedObject) => {
          if (shape === pastebufferType || pastebufferType === undefined)  {
            setPastebufferValue((prev: Record<string, [() => number[], (remove: boolean) =>  TrackedObject]>) => { 
              return { ...prev, [id]: [locate, slurp] } });
            if (pastebufferType === undefined) {
              setPastebufferType(shape);
            }
          }
        },
        removeFromBuffer: (id: string, shape: string) => {
          setPastebufferValue((prev: Record<string, [() => number[], (remove: boolean) => TrackedObject]>) => {
            let next = {...prev};
            delete next[id];
            if (Object.keys(next).length === 0) {
              setPastebufferType(undefined);
            }
            return next;
          });
          return Object.keys(pastebufferValue).length - 1;
        },
        slurp: (remove: boolean) => {
          // Call slurp on things in reverse order
          const toSort = Object.keys(pastebufferValue).map(id => [pastebufferValue[id][0](), id] as [number[], string]);
          toSort.sort((a, b) => -1*SortFunction(a[0], b[0]));

          const toReturn = toSort.map(([_, id]) => pastebufferValue[id][1](remove)).reverse();

          setPastebufferValue({});
          setPastebufferType(undefined);
          return toReturn;
        },
        shape: pastebufferType,
        rerender() {
          setGeneration((r) => r + 1);
        },
      }}>
        {(schema && config) && (
          <>
            {!!pastebufferType && <CopyToClipboardButton/>}
            {!!pastebufferType && <RemoveMultiSelectButton/>}
            <ExtraJsxContext.Provider value={{ renderExtraJsx: props.renderExtraJsx }}>
              <NodeElement node={schema[props.root || 'Root']} 
                schema={schema} 
                trackedObject={config}
                path={''}
                references={references}
                llmContext={llmContext}
                getMacro={props.getMacro}
                updateBreadcrumb={updateBreadcrumb}
                displayStacked={props.displayStacked}
                changesToRender={changesToRender}
                enableKeyIsValid={enableKeyIsValid}
              />
            </ExtraJsxContext.Provider>
          </>
        )}
        {(!schema || !config) &&
                    <div className="flex justify-center items-center h-full p-2">Distro is initializing...</div>
        }
      </PastebufferContext.Provider>
    </div>
  </>
}

const ExtraJsxContext = React.createContext<{ renderExtraJsx?: ExtraJsx } >({});

export const Editor = forwardRef(Editor_);

type DistroInitEvent = {
  detail: {
    types: string,
    migrations?: Record<string, (qType: any) => any>,
    macros?: Record<string, (_: any) => any>,
    programConfig?: Record<string, any> | null,
    root?: string,
    getMacro?: GetMacro,
    macroEditor?: boolean,
    showBreadcrumb?: boolean,
    displayStacked?: boolean,
    changesToRender?: ChangesToRender,
    enableKeyIsValid: EnableKeyIsValidFn,
    renderExtraJsx?: ExtraJsx,
  }
}

type DistroUpdateReactStateEvent = {
  detail: {
    props: Partial<DistroEditorProps>;
  }
}

type DistroLoadEvent = {
  detail: {
    value: any,
    events: ChangeSet,
    // expanded is an ExpandedSurvey
    expanded: Record<string, any> | null,
    userActivity: UserActivity[],
    references?: Record<string, string[]>,
    llmContext?: string
    state?: State
    author?: Author
    unpublishedEvents?: ChangeSet
    options?: Parameters<DistroEditorHandle['initialize']>[7]
  }
}

type DistroUpdateEvent = {

}

type DistroMergeEvent = {
  detail: {
    state: State,
  } | {
    events: ChangeSet,
    userActivity?: UserActivity
  }
}

const CopyToClipboardButton = () => {
  const { toast } = useToast();
  const pastebuffer = useContext(PastebufferContext);
  const doCopyToClipboard = useCallback(() => {
    const toAdd = pastebuffer.slurp(false);
    if (toAdd.length) {
      const toCopy = toAdd.map((t: any) => t.read());
      copyToClipboard(JSON.stringify(toCopy));
      toast({ description: 'Copied to clipboard', variant: 'success' });
    }
  }, [pastebuffer])

  // Adds ctrl+c (Windows) and cmd+c (Mac) keyboard shortcuts
  useEffect(() => {
    const onKeyDown: EventHandler<any> = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'c'){
        doCopyToClipboard();
      }
    }
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
    }
  }, [doCopyToClipboard])

  return (
    <button
      onClick={doCopyToClipboard}
      className='fixed rounded-full top-12 right-0 mr-2 mt-2 p-3 inline-flex items-center shadow-lg outline-none bg-blue-400 text-blue-700 focus:ring-2 focus:ring-blue-800 hover:bg-blue-500 hover:text-blue-900 transition duration-300 z-[65]'
      style={{ right: '35%' }}
    >
      <ClipboardDocumentIcon className="w-7" />
    </button>
  );
}

const RemoveMultiSelectButton = () => {
  const pastebuffer = useContext(PastebufferContext)

  // We make use of the pastebuffer slurp to remove multiple elements at a time
  const doSelectedRemoval = () => {
    const confirmed = window.confirm('Are you sure you want to delete the selected blocks?');
    if (confirmed) {
      pastebuffer.slurp(true)
    }
  }

  return (
    <button
      className='fixed rounded-full top-12 right-0 mr-2 mt-2 p-3 inline-flex items-center shadow-lg outline-none bg-red-400 text-red-700 focus:ring-2 focus:ring-red-800 hover:bg-red-500 hover:text-red-900 transition duration-300 z-[65]'
      onClick={doSelectedRemoval}
      style={{right: '42%'}}
    >
      <XMarkIcon className="w-7" />
    </button>)
}

const DistroInner = (props: { component: any }) => {
  let [types, setTypes] = useState('');
  let [value, setValue] = useState(undefined);
  let [root, setRoot] = useState('');
  let [migrations, setMigrations] = useState<Record<string, (qType: any) => any>>({});
  let [macros, setMacros] = useState<Record<string, (_: any) => any>>({});
  const [getMacro, setGetMacro] = useState<((_: any) => any) | undefined>();
  const [macroEditor, setMacroEditor] = useState<boolean>(false);
  const [showBreadcrumb, setShowBreadcrumb] = useState<boolean>(false);
  const [displayStacked, setDisplayStacked] = useState<boolean>(false);
  const [changesToRender, setChangesToRender] = useState<ChangesToRender | undefined>(undefined);
  const [renderExtraJsx, setRenderExtraJsx] = useState<ExtraJsx | undefined>(undefined);
  let [programConfig, setProgramConfig] = useState<Record<string, any> | null>(null);
  let [rerender, setRerender] = useState(0);
  const editor = useRef<React.ComponentRef<typeof Editor>>(null);
  const lastLoad = useRef<null | DistroLoadEvent>(null);

  useEffect(() => {
    // Don't bother if we don't have a component, or if it has been unmounted.
    if (!props.component) {
      return;
    }

    // This allows the clean-up function closure to retain a reference to the component, even if props.component
    // gets unmounted.
    const component = props.component;

    function init(e: DistroInitEvent) {
      if (e.detail.root) {
        setRoot(e.detail.root);
      }
      if (e.detail.migrations) {
        setMigrations(e.detail.migrations);
      }
      if (e.detail.macros) {
        setMacros(e.detail.macros);
      }
      if (e.detail.programConfig) {
        setProgramConfig(e.detail.programConfig);
      }
      if (e.detail.getMacro) {
        // We use this format here because otherwise react interprets the function
        // as a callback to the setState call, and executes it immediately.
        setGetMacro((prevState: GetMacro) => {
          return e.detail.getMacro;
        });
      }
      if (e.detail.macroEditor) {
        setMacroEditor(e.detail.macroEditor);
      }
      if (e.detail.programConfig?.experimental?.distroDisplayStacked) {
        setDisplayStacked(true);
      }
      if (e.detail.showBreadcrumb) {
        setShowBreadcrumb(e.detail.showBreadcrumb);
      }
      if (e.detail.changesToRender) {
        setChangesToRender(e.detail.changesToRender);
      }
      if (e.detail.renderExtraJsx) {
        // We use this format here because otherwise react interprets the function
        // as a callback to the setState call, and executes it immediately.
        setRenderExtraJsx((prevState: ExtraJsx | undefined) => {
          return e.detail.renderExtraJsx;
        });
      }
      setTypes(e.detail.types);
      console.log("Initialized types")

      // Retrigger load if this comes in late
      if (lastLoad.current) {
        component.dispatchEvent(new CustomEvent('distro-editor-load', lastLoad.current));
      }
    }

    function load(e: DistroLoadEvent) {
      editor.current!.initialize(e.detail.value, e.detail.author, e.detail.events, e.detail.userActivity, e.detail.references, e.detail.llmContext, e.detail.unpublishedEvents, e.detail.options);
      lastLoad.current = e;
      setRerender((r) => r + 1);
    }

    function merge(e: DistroMergeEvent) {
      if ((e.detail as any).state) {
        editor.current!.mergeState((e.detail as any).state);
      } else {
        editor.current!.mergeEvents((e.detail as any).events, (e.detail as any).userActivity, (e.detail as any).unpublishedEvents);
      }
    }

    function updateReactState(e: DistroUpdateReactStateEvent) {
      if (e.detail.props.showBreadcrumb !== undefined) {
        setShowBreadcrumb(e.detail.props.showBreadcrumb);
      }
    }

    component.addEventListener('distro-editor-init', init);
    component.addEventListener('distro-editor-load', load);
    component.addEventListener('distro-editor-merge', merge);
    component.addEventListener('distro-editor-react-state', updateReactState);

    // Once listeners are added, let DistroEditor_ know so it can fire its 'load' event
    component.dispatchEvent(new CustomEvent('innerListenersReadyEvent'));
    console.log('DistroInner listening for events');

    // Cleanup listeners
    return () => {
      component.removeEventListener('distro-editor-init', init);
      component.removeEventListener('distro-editor-load', load);
      component.removeEventListener('distro-editor-merge', merge);
      component.removeEventListener('distro-editor-react-state', updateReactState);
    };
  }, []);

  return <>
    <Editor 
      ref={editor} 
      types={types} 
      value={value} 
      root={root} 
      rerender={rerender} 
      migrations={migrations} 
      macros={macros} 
      programConfig={programConfig}
      onChange={(value, state, events, expanded) => {
        props.component.dispatchEvent(new CustomEvent('distro-editor-update', { detail: { 
          value, 
          state,
          events,
          expanded,
        } }))
      }}
      getMacro={getMacro}
      macroEditor={macroEditor}
      showBreadcrumb={showBreadcrumb}
      displayStacked={displayStacked}
      renderExtraJsx={renderExtraJsx}
    />
  </>
}

class DistroEditorComponent extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    const shadowRoot = this.attachShadow({ mode: 'open' });
        
    let styleSheet = new CSSStyleSheet;
    // h/t https://css-tricks.com/the-cleanest-trick-for-autogrowing-textareas/
    styleSheet.replaceSync(styling.default + `
          .grow-wrap {
            display: grid;
          }
          .grow-wrap::after {
            content: attr(data-replicated-value) " ";
            white-space: pre-wrap;
            visibility: hidden;
          }
          .grow-wrap > textarea {
            resize: none;
            overflow: hidden;
          }
          .grow-wrap > textarea,
          .grow-wrap::after {
            border: 1px solid #ccc;
            padding: 0.5rem;
            font: inherit;
            grid-area: 1 / 1 / 2 / 2;
          } 
        `);
    shadowRoot.adoptedStyleSheets = [styleSheet];

    // Since distro-editor is using a shadow DOM, it doesn't have access to 
    // our regular stylesheets that we're injecting into the page. 
    // This passes the CSS styles through to the shadow root. 
    const mainStyles = [...document.styleSheets]
      .map(sheet => [...sheet.cssRules || []].map(rule => rule.cssText).join(''))
      .join('');
    shadowRoot.innerHTML = `
        <style>${mainStyles}</style>
    `;

    const root = createRoot(shadowRoot);
    root.render(<StrictMode><DistroInner component={this} /></StrictMode>);
  }
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      "distro-editor": any;
    }
  }
}

if (customElements.get('distro-editor') === undefined) {
  customElements.define('distro-editor', DistroEditorComponent);
}

export type ChangesToRender = {
  additions: {
    object: TrackedObject,
    location?: Location,
    authors?: Author[]
  }[],
  removals: {
    object: TrackedObject,
    location?: Location,
    authors?: Author[]
  }[]
}

type DistroEditorProps = {
  types: string,
  name: string,
  migrations?: Record<string, (qType: TrackedRecord) => void>,
  macros?: Record<string, (qType: any) => void>,
  programConfig?: Record<string, any> | null,
  value?: any,
  onChange?: (v: any, collabState: State, events: ChangeSet, expanded: Record<string, any> | null, myActivity?: UserActivity) => any,
  references?: Record<string, string[]>
  llmContext?: string,
  getMacro?: GetMacro,
  macroEditor?: boolean,
  showBreadcrumb?: boolean,
  displayStacked?: boolean,
  changesToRender?: ChangesToRender,
  enableKeyIsValid?: EnableKeyIsValidFn,
  renderExtraJsx?: ExtraJsx,
}

type EnableKeyIsValidFn = (key: string, obj: any, options?: { allowVersions?: ['legacy'] }) => boolean;
export type DistroEditorHandle = {
  initialize: (value: any, author?: Author, events?: ChangeSet, userActivity?: UserActivity[], references?: Record<string, string[]>, llmContext?: string, unpublishedEvents?: ChangeSet, options?: { enableKeyIsValid?: EnableKeyIsValidFn, debug?: boolean, changesToRender?: ChangesToRender }) => void,
  update: (value: any) => void,
  mergeState: (collabState: State) => void,
  mergeEvents: (events: ChangeSet, userActivity?: UserActivity[], unpublishedEvents?: ChangeSet) => void,
  getPrunedEvents: (state: State) => ChangeSet
}

const DistroEditor_: React.ForwardRefRenderFunction<DistroEditorHandle, DistroEditorProps> = (props: DistroEditorProps, ref) => {
  const editor = useRef<any>();
  const initialized = useRef<boolean>(false);
  const toInit = useRef<any>(null);
  const initialValue = useRef<any>(props.value);
  const [innerListenersReady, setInnerListenersReady] = useState<boolean>(false);
  const [initArgs, setInitArgs] = useState<{ value: any, events?: ChangeSet, userActivity?: UserActivity[], references?: Record < string, string[]>, llmContext?: string, author?: Author, unpublishedEvents?: ChangeSet, options?: Parameters<DistroEditorHandle['initialize']>[7]} | null>(null);

  useImperativeHandle(ref, () => ({
    initialize: (value: any, author?: Author, events?: ChangeSet, userActivity?: UserActivity[], references?: Record<string, string[]>, llmContext?: string, unpublishedEvents?: ChangeSet, options?: Parameters<DistroEditorHandle['initialize']>[7]) => {
      // Try dispatching a load event. If DistroInner has already loaded and set up its event listeners, then this will work.
      // Otherwise, we will try again below in the useEffect after we receive confirmation that the listeners on DistroInner are ready.
      // This dispatch here is still necessary because DistroInner may set itself up before DistroEditor_, meaning we can't be sure
      // we will ever receive a innerListenersReadyEvent here.
      console.log('DistroEditorHandle sending load event - A');
      editor.current.dispatchEvent(new CustomEvent('distro-editor-load', {
        detail: { 
          value,
          events,
          userActivity,
          references,
          llmContext,
          loc: 'outer',
          author,
          unpublishedEvents,
          options
        }
      }));
      setInitArgs({ value, events, userActivity, references, llmContext, author, unpublishedEvents, options });
    },
    update: (value: any) => {

    },
    mergeState: (collabState: State) => {
      editor.current.dispatchEvent(new CustomEvent('distro-editor-merge', {
        detail: { 
          state: collabState
        } 
      }));
    },
    mergeEvents: (events: ChangeSet, userActivity?: UserActivity[], unpublishedEvents?: ChangeSet) => {
      editor.current.dispatchEvent(new CustomEvent('distro-editor-merge', {
        detail: { 
          events: events,
          userActivity: userActivity,
          unpublishedEvents: unpublishedEvents
        } 
      }));
    },
    getPrunedEvents: (collabState: State) => {
      return getCompressedEvents(collabState);
    }
  }));

  useEffect(() => {
    if (props.types.endsWith('.ts') || props.types.endsWith('.tsx') || props.types.endsWith('.d.ts')) {
      (async () => {
        let rawCopies = await Promise.allSettled([props.types, '/' + props.types].map(async (path) => {
          let resp = await fetch(path + '?random=' + Math.random());
          if (resp.status !== 200 || resp.headers.get('Content-Type')?.includes('text/html')) {
            return null;
          }
          return await resp.text();
        }));
        let source = null;
        for (const raw of rawCopies) {
          if(raw.status === 'rejected' || !raw.value) {
            continue;
          }
          if (raw.value.includes('export type ' + props.name)) {
            source = raw.value;
          }
        }
        if (!source) {
          console.log('A');
          let scriptTags = document.getElementsByTagName('script');
          let maps = await Promise.allSettled(Array.from(scriptTags).map(async (script) => {
            const resp = await fetch(script.src + '.map?random=' + Math.random());
            if (!resp.ok) return null;
            const contentType = resp.headers.get('Content-Type') || '';
            if (!contentType.includes('application/json')) return null;
            return await resp.json();
          }));
          console.log('B');
          for (const map of maps.filter(m => m !== null)) {
            if(map.status === 'rejected' || !map.value) {
              continue;
            }
            let i = 0;
            for (const s of map.value.sources) {
              if (s.includes(props.types)) {
                source = map.value.sourcesContent[i]
                break;
              }
              i += 1;
            }
          }
        } 
        if (source) {
          console.log('C', props.name);
          if (!initialized.current) {
            if (editor.current) {
              console.log("bzn current");
              editor.current.dispatchEvent(new CustomEvent('distro-editor-init', {
                detail: { 
                  types: source,
                  value: props.value,
                  migrations: props.migrations,
                  programConfig: props.programConfig,
                  macros: props.macros,
                  root: props.name,
                  getMacro: props.getMacro,
                  macroEditor: props.macroEditor,
                  showBreadcrumb: props.showBreadcrumb,
                  displayStacked: props.displayStacked,
                  changesToRender: props.changesToRender,
                  enableKeyIsValid: props.enableKeyIsValid,
                  renderExtraJsx: props.renderExtraJsx
                } 
              }));
              if (initialValue.current) {
                console.log('DistroEditorHandle sending load event - C');
                editor.current.dispatchEvent(new CustomEvent('distro-editor-load', {
                  detail: { 
                    value: initialValue.current,
                    loc: 'inner'
                  }
                }));
              }
              initialized.current = true;
            } else {
              console.log("Stashing for later");
              toInit.current = {
                detail: { 
                  types: source,
                  value: props.value,
                  migrations: props.migrations,
                  programConfig: props.programConfig,
                  macros: props.macros,
                  root: props.name,
                  getMacro: props.getMacro,
                  macroEditor: props.macroEditor,
                  showBreadcrumb: props.showBreadcrumb,
                  displayStacked: props.displayStacked,
                  changesToRender: props.changesToRender,
                  enableKeyIsValid: props.enableKeyIsValid,
                  renderExtraJsx: props.renderExtraJsx
                } 
              };
            }
          }
        } else {
          alert('Failed to find source');
        }
      })();
    }
  }, []);
    
  useEffect(() => {
    if (editor.current) {
      const listener = (e: DistroLoadEvent) => {
        if (props.onChange && e.detail.state) props.onChange(e.detail.value, e.detail.state, e.detail.events, e.detail.expanded);
      };
      const el = editor.current;
      el.addEventListener('distro-editor-update', listener);

      return () => {
        if (el) {
          el.removeEventListener('distro-editor-update', listener);
        }
      }
    }
  }, [editor]);

  // This allows updates to the "showBreadcrumb" prop to get propagated to the actual Distro component
  useEffect(() => {
    if (editor.current) {
      editor.current.dispatchEvent(
        new CustomEvent(
          'distro-editor-react-state',
          { detail: { props: { showBreadcrumb: props.showBreadcrumb } } }
        )
      );
    }
  }, [props.showBreadcrumb]);

  // If DistroEditor_ initialized before DistroInner, than inner might have missed the first distro-editor-load event fired
  // from initialize() above. Thus we listen for confirmation that its listeners are ready, and fire another one.
  useEffect(() => {
    function innerListenersReadyCallback() {
      setInnerListenersReady(true);
    }

    // This allows the clean-up function closure to retain a reference to the component, even if editor.current
    // gets unmounted.
    const editorReference = editor.current;

    if (initArgs && innerListenersReady) {
      console.log('DistroEditorHandle sending load event - B', initArgs.options);
      editorReference.dispatchEvent(new CustomEvent('distro-editor-load', { detail: initArgs }));
    } else {
      editorReference.addEventListener('innerListenersReadyEvent', innerListenersReadyCallback);
    }

    // Clean up
    return () => {
      editorReference.removeEventListener("innerListenersReadyEvent", innerListenersReadyCallback);
    };
  }, [innerListenersReady, initArgs]);

  useEffect(() => {
    if (props.types.endsWith('.ts') || props.types.endsWith('.tsx') || props.types.endsWith('.d.ts')) return;

    // Janky AF because the editor hasn't been set up immediately
    // TODO(Riley/Ben) should the below be moved up to the above useEffect which waits for DistroInner's innerListenersReadyEvent ?
    setTimeout(() => {
      editor.current.dispatchEvent(new CustomEvent('distro-editor-init', {
        detail: { 
          types: props.types,
          value: props.value,
          migrations: props.migrations,
          programConfig: props.programConfig,
          macros: props.macros,
          root: props.name,
          getMacro: props.getMacro,
          macroEditor: props.macroEditor,
          showBreadcrumb: props.showBreadcrumb,
          displayStacked: props.displayStacked,
          changesToRender: props.changesToRender,
          enableKeyIsValid: props.enableKeyIsValid,
          renderExtraJsx: props.renderExtraJsx
        } 
      }));
      editor.current.dispatchEvent(new CustomEvent('distro-editor-load', {
        detail: { 
          value: props.value,
          loc: 'innerrawtypes'
        }
      }));
    }, 100)

  }, [editor]);

  return <>
    <distro-editor ref={editor}></distro-editor>
  </>
}

export type ComputedDependentNotifs = { action: 'COMPUTED', targetField: string };
export type ExtraJsxActions = ComputedDependentNotifs;
export type ExtraJsx = (action: ExtraJsxActions) => JSX.Element | null;

export const DistroEditor = forwardRef(DistroEditor_);

//if ((module as any).hot) (module as any).hot.accept();

export default Editor;
